import {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  FiChevronDown,
  FiChevronRight,
  FiCheck,
  FiX,
  FiStar,
  FiMapPin,
  FiClock,
  FiBriefcase,
  FiPlus,
  FiRefreshCw,
  FiGlobe,
  FiSearch,
  FiExternalLink,
  FiMoreHorizontal,
  FiUpload,
  FiTrash2,
  FiFileText,
  FiSliders,
  FiGrid,
  FiList,
  FiAlertCircle,
  FiMinusCircle,
  FiBookmark,
} from "react-icons/fi";
import { Spark, StarFilled } from "../components/ui/icons";
import { useAuth } from "../context/AuthContext";
import { fetchWithAuth } from "../utils/authFetch";
import { newBatchId } from "../utils/batchId";
import { creditsChanged } from "../utils/useCredits";
import {
  getUserSettings,
  invalidateUserSettingsCache,
  updateCachedUserSettings,
} from "../lib/userSettingsService";
import { getCachedJobsList, primeJobsListCache } from "../lib/jobsListService";
import {
  getCachedJobDetail,
  getJobDetail,
  primeJobDetailCache,
} from "../lib/jobDetailService";
import Skeleton, { SkeletonText, FadeIn, useFillCount } from "../components/Skeleton";
import { SavedJobsView, SavedJobsSkeleton } from "./Watchlist";
import CompanyLogo from "../components/CompanyLogo";
import PlatformMark, { PLATFORMS, PlatformLogo } from "../components/PlatformMark";
import { usePlan } from "../utils/usePlan";
import PlanLock from "../components/PlanLock";
import JobDetailView, {
  Btn,
  IconBtn,
  Tabs,
  Tab,
  TabCount,
  Tag,
  jobIdOf,
  postedRel,
  firstCompany,
  initialOf,
  companyColor,
  locationShort,
  DetailWrap,
  JDCard,
} from "../components/JobDetailView";
import { useLockPageScroll } from "../utils/useLockPageScroll";


/* ─────────────────────────────────────────────────────────
   Layout shell
───────────────────────────────────────────────────────── */
const Shell = styled.div`
  background: var(--bg);
  color: var(--ink);
  font-family: "Geist", ui-sans-serif, system-ui, -apple-system, "Segoe UI",
    sans-serif;
  font-size: 14px;
  line-height: 1.45;
  margin: -2.5rem;
  min-height: calc(100vh + 5rem);
  /* Bleed past MainContent's padding on every edge, including the strip it
     reserves below 1024px for the fixed menu button, so Top's surface colour
     runs up behind the button instead of leaving a band of page background.
     Top carries the button's clearance as padding-top. min-height tracks the
     total vertical bleed (top + bottom) so the background still fills. */
  @media (max-width: 1024px) {
    margin: -4.75rem -1.5rem -1.5rem;
    min-height: calc(100vh + 6.25rem);
  }
  @media (max-width: 768px) {
    margin: -4.25rem -1rem -1rem;
    min-height: calc(100vh + 5.25rem);
  }
  @media (max-width: 450px) {
    margin: -4rem -0.75rem -0.75rem;
    min-height: calc(100vh + 4.75rem);
  }
  @media (max-width: 390px) {
    margin: -3.8rem -0.5rem -0.5rem;
    min-height: calc(100vh + 4.3rem);
  }
`;

/* The title sits where Outreach's does: 30px down (24px on phones), or 20px when a job's breadcrumb sits above it. */
const Top = styled.div`
  --top-gap: ${({ $crumbs }) => ($crumbs ? "20px" : "30px")};
  background: var(--surface);
  border-bottom: 1px solid var(--line);
  padding: var(--top-gap) 36px 14px;
  /* Shorthand first in each block, then padding-top, or the shorthand wins. */
  @media (max-width: 1024px) {
    padding-top: calc(var(--top-gap) + 4.75rem);
  }
  @media (max-width: 768px) {
    --top-gap: ${({ $crumbs }) => ($crumbs ? "14px" : "24px")};
    padding: var(--top-gap) 18px 12px;
    padding-top: calc(var(--top-gap) + 4.25rem);
  }
  @media (max-width: 450px) {
    padding-top: calc(var(--top-gap) + 4rem);
  }
  @media (max-width: 390px) {
    padding-top: calc(var(--top-gap) + 3.8rem);
  }
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  /* Phone: title left, Add jobs top-right. Wrapped, the long title meta line
     pushes the button onto a row of its own where it sits stranded at the left
     edge, reading as though it belongs to the tab strip below it. */
  @media (max-width: 768px) {
    gap: 8px 12px;
    align-items: flex-start;
    margin-bottom: 10px;
  }
`;

/* Crumbs + title + meta. Shrinks so the header actions keep their width.
   On phones it dissolves so crumbs and title become direct flex items of
   TopRow: the crumbs claim a row of their own, which leaves the title and the
   Add button sharing the next row and sitting on the same line. */
const TopLead = styled.div`
  min-width: 0;
  flex: 1;
  @media (max-width: 768px) {
    display: contents;
  }
`;
/* Primary page actions, on the title's line at the right of the header. */
const TopActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-shrink: 0;
    justify-content: flex-end;
    /* Top of the title, not its centre, so a title that wraps to two lines
       keeps the button beside its first line rather than dragging it down. */
    align-self: flex-start;
  }
`;
const Crumbs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-muted);
  margin-bottom: 6px;
  /* A direct flex item of TopRow once TopLead dissolves: claim a whole row so
     the title and the Add button pair up on the next one. */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    margin-bottom: 0;
  }
`;
const Crumb = styled.button`
  color: var(--ink-muted);
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: var(--ink);
  }
  ${({ $current }) =>
    $current &&
    css`
      color: var(--ink);
      font-weight: 500;
      cursor: default;
    `}
  /* Text crumbs: the global 44px touch floor turned this line into a tall
     empty band above the title. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const CrumbSep = styled.span`
  color: var(--line-strong);
`;
const PageTitle = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 38px;
  line-height: 1;
  color: var(--ink);
  letter-spacing: -0.01em;
  margin: 0;
  font-weight: 400;
  /* Direct flex item of TopRow on phones — take the row so the button is
     pushed to the right edge, level with these words. */
  @media (max-width: 768px) {
    font-size: 30px;
    flex: 1 1 auto;
    min-width: 0;
  }
`;
const TitleMeta = styled.span`
  font-family: "Geist", sans-serif;
  font-size: 13.5px;
  color: var(--ink-muted);
  margin-left: 16px;
  font-weight: 400;
  letter-spacing: 0;
  b {
    color: var(--ink);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .accent b {
    color: var(--accent);
  }
  /* Dropped on phones. Four counts is more than a small screen should spend on
     header chrome, and each number is already visible in the views below. */
  @media (max-width: 768px) {
    display: none;
  }
`;


/* ─────────────────────────────────────────────────────────
   Search + chip filters
───────────────────────────────────────────────────────── */
export const SearchWrap = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;
export const Search = styled.div`
  flex: 0 1 460px;
  max-width: 460px;
  position: relative;
  display: flex;
  align-items: center;
  svg.lead {
    position: absolute;
    left: 11px;
    color: var(--ink-muted);
    pointer-events: none;
  }
  input {
    width: 100%;
    height: 34px;
    padding: 0 12px 0 36px;
    border-radius: 8px;
    border: 1px solid var(--line);
    background: var(--surface-tint);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    transition: border-color 0.12s, background 0.12s, box-shadow 0.12s;
    outline: 0;
  }
  input::placeholder {
    color: var(--ink-muted);
  }
  input:focus {
    background: var(--surface);
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
  /* Keep 34px, level with the Filters button, instead of the 44px floor. */
  @media (max-width: 450px) {
    input {
      min-height: 0;
    }
  }
`;
export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  background: var(--surface);
  border: 1px solid var(--line);
  font-size: 12.5px;
  color: var(--ink-soft);
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;
  flex-shrink: 0;
  /* Chips keep their own height; the 44px floor made them chunky. */
  @media (max-width: 450px) {
    min-height: 0;
  }
  &:hover {
    background: var(--surface-tint);
    border-color: var(--line-strong);
    color: var(--ink);
  }
  ${({ $active }) =>
    $active &&
    css`
      /* Selected filter: light blue (blue = what you've picked or can act on). */
      background: var(--accent-soft);
      color: var(--accent);
      border-color: color-mix(in srgb, var(--accent) 45%, transparent);
      &:hover {
        background: var(--accent-soft);
        border-color: var(--accent);
        color: var(--accent);
      }
    `}
`;
// Dropdown trigger: fixed width so swapping the label for a selected value
// (e.g. "Category" → "Banking & Financial Services") never reflows the row.
const FilterTrigger = styled(Chip)`
  /* Hold ~156px when there's room, but shrink-and-truncate to stay on one line. */
  flex: 0 1 156px;
  min-width: 96px;
  justify-content: space-between;
`;
// The label/value text truncates inside the fixed-width trigger.
const TriggerText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
`;
// Chevron sits flush right and rotates open/closed.
const TriggerCaret = styled(FiChevronDown)`
  flex-shrink: 0;
  opacity: 0.7;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;
// Outer frame: clips and isolates so overscroll bounce stays inside the
// menu. The actual scroll happens on the inner MenuScroll child.
const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  min-width: 220px;
  z-index: 50;
  overflow: hidden;
  isolation: isolate;
`;
const MenuScroll = styled.div`
  padding: 6px;
  max-height: 280px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
  &:hover {
    background: var(--bg-2);
  }
  /* Link items (as="a") picked up the 44px floor; div items didn't, so one
     menu mixed row heights. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const MenuDivider = styled.div`
  height: 1px;
  background: var(--line-soft);
  margin: 4px 0;
`;

/* ─────────────────────────────────────────────────────────
   Filters button + panel (single entry point for all filters)
───────────────────────────────────────────────────────── */
const FiltersButton = styled(Chip)`
  height: 34px;
  padding: 0 14px;
  font-size: 13px;
`;
// Active-filter count badge on the Filters button.
const FilterCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--bg);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;
const FilterPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  /* $alignEnd opens leftward from the button's right edge, for a trigger at the row's end. */
  ${({ $alignEnd }) => ($alignEnd ? "right: 0;" : "left: 0;")}
  width: 300px;
  max-width: calc(100vw - 32px);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  z-index: 60;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const PanelTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
`;
export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
export const PanelFieldLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-muted);
`;
// Full-width select-style trigger used for fields inside the Filters panel.
const BlockTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--surface);
  border: 1px solid var(--line);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  white-space: nowrap;
  justify-content: space-between;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:hover {
    border-color: var(--line-strong);
  }
  ${({ $active }) =>
    $active &&
    css`
      border-color: var(--ink-3);
    `}
`;
// Subtle text button to clear everything, shown at the bottom of the panel.
export const ClearLink = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 2px;
  background: none;
  border: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-soft);
  cursor: pointer;
  transition: color 0.12s;
  &:hover {
    color: var(--ink);
  }
`;

/* ─────────────────────────────────────────────────────────
   Canvas / grid
───────────────────────────────────────────────────────── */
const Canvas = styled.div`
  padding: 0;
`;
const GridWrap = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px 36px 60px;
  @media (max-width: 768px) {
    padding: 16px 18px 40px;
  }
`;
const CountRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: var(--ink-muted);
  margin-bottom: 16px;
  b {
    color: var(--ink);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 14px;
  /* A 340px minimum is wider than a 360–375px phone's content box, so the
     single column overflowed the right edge. */
  @media (max-width: 450px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* ─────────────────────────────────────────────────────────
   Job card
───────────────────────────────────────────────────────── */
const JCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 20px 22px 18px;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.06s;
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-md);
  }
  &:active {
    transform: translateY(0.5px);
  }
  ${({ $picked }) =>
    $picked &&
    css`
      border-color: var(--secondary);
      box-shadow: 0 0 0 1.5px var(--secondary), 0 0 0 5px var(--secondary-soft);
      transition: none;
      &:hover {
        border-color: var(--secondary);
      }
    `}
  /* Closed posting: visibly inactive, but still fully openable. */
  ${({ $closed }) =>
    $closed &&
    css`
      border-style: dashed;
      background: var(--bg-2);
    `}
`;
const JCardStar = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--ink-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
  /* Stay a 30px square; the 44px floor stretched its hover into a tall pill. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const JCardHead = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
  padding-right: 36px;
`;
const JCompany = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.2;
  .nm {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const JCompanyType = styled.div`
  font-size: 11.5px;
  color: var(--ink-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const JTitle = styled.h3`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 22px;
  line-height: 1.2;
  color: var(--ink);
  letter-spacing: -0.005em;
  margin: 0;
  padding-right: 36px;
  font-weight: 400;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* Lock to exactly two lines so each card's lower rows align */
  min-height: calc(22px * 1.2 * 2);
  max-height: calc(22px * 1.2 * 2);
`;
const JSnippet = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink-soft);
  margin: 10px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* Reserve 2 lines so the rows below align across cards */
  min-height: calc(13px * 1.5 * 2);
`;
const JMeta = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px 14px;
  font-size: 12px;
  color: var(--ink-muted);
  position: relative;
  .item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .item.clickable {
    cursor: pointer;
    border-radius: 6px;
    padding: 1px 6px;
    margin: -1px -6px;
    transition: background 0.12s, color 0.12s;
  }
  .item.clickable:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
`;
const JPills = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 5px;
  flex-wrap: nowrap;
  overflow: hidden;
  height: 22px;
  /* Hide pills that overflow rather than wrap, so the View-details row
     stays at the same vertical position across every card. */
  & > * {
    flex: 0 0 auto;
  }
`;
const JFoot = styled.div`
  margin-top: 22px;
  padding-top: 14px;
  border-top: 1px solid var(--line-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

/* ─────────────────────────────────────────────────────────
   Skeleton placeholder card — mirrors JCard dimensions/layout so the
   grid reserves identical space and nothing pops in or shifts.
───────────────────────────────────────────────────────── */
export function JobCardSkeleton() {
  return (
    <JCard as="div" style={{ cursor: "default" }} aria-hidden="true">
      <JCardHead>
        <Skeleton width={40} height={40} radius="11px" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <Skeleton width="55%" height={13} mb={6} />
          <Skeleton width="38%" height={11} />
        </div>
      </JCardHead>
      {/* Title: two locked lines, matching JTitle min-height */}
      <Skeleton width="92%" height={20} mb={8} />
      <Skeleton width="70%" height={20} />
      {/* Snippet: two reserved lines, matching JSnippet */}
      <div style={{ marginTop: 14 }}>
        <Skeleton width="100%" height={12} mb={7} />
        <Skeleton width="80%" height={12} />
      </div>
      {/* Meta row */}
      <div style={{ display: "flex", gap: 14, marginTop: 16 }}>
        <Skeleton width={80} height={12} />
        <Skeleton width={64} height={12} />
      </div>
      {/* Pills row (fixed 22px height like JPills) */}
      <div style={{ display: "flex", gap: 5, marginTop: 12 }}>
        <Skeleton width={72} height={22} radius="999px" />
        <Skeleton width={92} height={22} radius="999px" />
      </div>
      {/* Footer */}
      <JFoot>
        <Skeleton width={96} height={28} radius="8px" />
        <Skeleton width={84} height={28} radius="8px" />
      </JFoot>
    </JCard>
  );
}

// Inline stand-in for a number that hasn't loaded yet, so a count never reads 0 before it's known.
function CountSkeleton({ width = 18, height = 10 }) {
  return (
    <Skeleton inline width={width} height={height} radius="4px" style={{ verticalAlign: "middle" }} />
  );
}

// A grid of placeholder cards, enough to fill the screen at the current width.
function JobGridSkeleton() {
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <Grid ref={ref} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <JobCardSkeleton key={i} />
      ))}
    </Grid>
  );
}

/* ─────────────────────────────────────────────────────────
   Job detail skeleton — mirrors JobDetailView's header card + body
   cards so the inline detail view fills in without blank/pop-in.
───────────────────────────────────────────────────────── */
function JobDetailSkeleton() {
  return (
    <DetailWrap aria-hidden="true">
      <Skeleton width={120} height={28} radius="8px" mb={16} />
      <JDCard>
        <div style={{ display: "flex", gap: 18, marginBottom: 18 }}>
          <Skeleton width={56} height={56} radius="14px" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="40%" height={13} mb={10} />
            <Skeleton width="78%" height={34} mb={10} />
            <Skeleton width="55%" height={34} />
          </div>
        </div>
        {/* Meta line */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <Skeleton width={110} height={13} />
          <Skeleton width={90} height={13} />
          <Skeleton width={130} height={13} />
        </div>
        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <Skeleton width={150} height={36} radius="9px" />
          <Skeleton width={120} height={36} radius="9px" />
        </div>
      </JDCard>
      <JDCard>
        <Skeleton width={160} height={18} mb={16} />
        <SkeletonText lines={6} height={13} gap={10} />
        <div style={{ marginTop: 18 }}>
          <SkeletonText lines={5} height={13} gap={10} lastWidth="45%" />
        </div>
      </JDCard>
    </DetailWrap>
  );
}

/* ─────────────────────────────────────────────────────────
   Empty state
───────────────────────────────────────────────────────── */
const Empty = styled.div`
  display: grid;
  place-items: center;
  text-align: center;
  padding: 80px 20px;
  /* Accent-tinted tile — matches the Dashboard empty state. */
  .art {
    width: 96px;
    height: 96px;
    border-radius: 24px;
    background: linear-gradient(155deg, var(--accent-soft), var(--surface-tint));
    border: 1px solid var(--line);
    color: var(--accent);
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    box-shadow: var(--shadow-md);
    box-shadow: 0 8px 22px -10px color-mix(in srgb, var(--accent) 35%, transparent);
  }
  h3 {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 26px;
    margin: 0 0 6px;
    color: var(--ink);
    font-weight: 400;
  }
  p {
    color: var(--ink-muted);
    max-width: 360px;
    margin: 0 auto 12px;
    font-size: 13.5px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Picker bar (carry-over for pickFor=find-candidates mode)
───────────────────────────────────────────────────────── */
const PickerBar = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 14px;
  max-width: 1320px;
  margin: 14px auto 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13.5px;
  color: var(--ink);
  flex-wrap: wrap;
  /* Shell bleeds to the screen edge on phones; inset to the grid's gutter so
     the rounded bar doesn't sit flush against both sides. */
  @media (max-width: 768px) {
    margin: 12px 18px 0;
  }
`;

/* ─────────────────────────────────────────────────────────
   Tracked companies view
───────────────────────────────────────────────────────── */
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const TrackWrap = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px 36px 60px;
  @media (max-width: 768px) {
    padding: 16px 18px 40px;
  }
`;
const TrackBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: linear-gradient(
    135deg,
    var(--accent-soft),
    var(--surface) 78%
  );
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
  .icon {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: var(--accent);
    color: var(--on-fill);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .h {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 20px;
    color: var(--ink);
    letter-spacing: -0.005em;
  }
  .sub {
    font-size: 12.5px;
    color: var(--ink-soft);
    margin-top: 2px;
    max-width: 620px;
    line-height: 1.45;
  }
  .cta {
    margin-left: auto;
    flex-shrink: 0;
  }
  @media (max-width: 640px) {
    flex-wrap: wrap;
    .cta {
      margin-left: 0;
      flex-basis: 100%;
      justify-content: center;
    }
  }
`;
const TrackSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--ink-muted);
  margin-bottom: 14px;
  flex-wrap: wrap;
  b {
    color: var(--ink);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .accent b {
    color: var(--accent);
  }
  .dot-sep {
    color: var(--line-strong);
  }
`;
const TrackHeadRow = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr 168px 110px 200px;
  align-items: center;
  gap: 14px;
  padding: 0 18px 8px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: var(--ink-muted);
  @media (max-width: 1100px) {
    display: none;
  }
`;
const TrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const TrackRow = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr 168px 110px 200px;
  align-items: center;
  gap: 14px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 13px;
  padding: 14px 18px;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-md);
  }
  ${({ $paused }) =>
    $paused &&
    css`
      opacity: 0.72;
    `}
  @media (max-width: 1100px) {
    grid-template-columns: 44px 1fr auto;
    row-gap: 12px;
  }
  @media (max-width: 640px) {
    grid-template-columns: 40px 1fr auto;
    gap: 10px 12px;
    padding: 12px 14px;
    border-radius: 11px;
  }
`;
const TrackName = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.2;
`;
const TrackType = styled.div`
  font-size: 12px;
  color: var(--ink-muted);
  margin-top: 2px;
`;
const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 1100px) {
    grid-column: 2;
  }
`;
const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 99px;
  width: fit-content;
  background: var(--bg-2);
  color: var(--ink-muted);
  ${({ $type }) =>
    $type === "active" &&
    css`
      background: var(--success-soft);
      color: var(--success);
    `}
  ${({ $type }) =>
    $type === "syncing" &&
    css`
      background: var(--accent-soft);
      color: var(--accent-ink);
    `}
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 99px;
    background: currentColor;
  }
  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;
const TrackChecked = styled.div`
  font-size: 11.5px;
  color: var(--ink-muted);
  b {
    color: var(--accent);
    font-weight: 600;
  }
`;
const TrackStat = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  background: none;
  border: 0;
  font: inherit;
  padding: 4px;
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? "var(--surface-tint)" : "transparent"};
  }
  .num {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 24px;
    line-height: 1;
    color: var(--ink);
  }
  .num.accent {
    color: var(--accent);
  }
  .num.muted {
    color: var(--ink-faint);
  }
  .lbl {
    font-size: 10.5px;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 4px;
  }
  @media (max-width: 1100px) {
    grid-column: 2;
  }
  @media (max-width: 1100px) {
    justify-self: start;
    flex-direction: row;
    align-items: baseline;
    gap: 6px;
    padding: 0;
    .num {
      font-size: 20px;
    }
    .lbl {
      margin-top: 0;
    }
  }
  /* Inline "12 jobs" text here; the 44px floor padded its row out. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const TrackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;
const Chevron = styled.span`
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  padding: 4px;
  color: var(--ink-muted);
  transition: transform 0.18s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;
// One company can be tracked via several careers URLs; the group wraps the
// company header row and its (collapsible) per-link sub-rows.
const TrackGroupWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const TrackSubList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 2px 0 4px 21px;
  padding-left: 22px;
  border-left: 2px solid var(--line);
  @media (max-width: 640px) {
    margin-left: 10px;
    padding-left: 10px;
  }
`;
const TrackSubRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 150px 84px 40px;
  align-items: center;
  gap: 14px;
  background: var(--surface-tint);
  border: 1px solid var(--line);
  border-radius: 11px;
  padding: 11px 16px;
  .link-main {
    min-width: 0;
  }
  .host {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .host:hover {
    color: var(--accent);
    text-decoration: underline;
  }
  /* The site link is a text line; the 44px floor on links doubled the row. */
  @media (max-width: 450px) {
    .host {
      min-height: 0;
    }
  }
  .host svg:last-child {
    color: var(--ink-faint);
    flex-shrink: 0;
  }
  .host .ctry {
    font-weight: 500;
    color: var(--ink-muted);
  }
  .sub {
    font-size: 11.5px;
    color: var(--ink-muted);
    margin-top: 3px;
  }
  .sub b {
    color: var(--accent);
    font-weight: 600;
  }
  .sub-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
  .sub-stat .num {
    font-family: "Instrument Serif", Georgia, serif;
    font-size: 19px;
    line-height: 1;
    color: var(--ink);
  }
  .sub-stat .num.muted {
    font-family: inherit;
    font-size: 12.5px;
    color: var(--ink-faint);
  }
  .sub-stat .lbl {
    font-size: 9.5px;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  ${({ $off }) =>
    $off &&
    css`
      opacity: 0.62;
    `}
  @media (max-width: 1100px) {
    grid-template-columns: 1fr auto;
    row-gap: 10px;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;
    gap: 8px 10px;
    padding: 10px 12px;
    & > :nth-child(1) {
      grid-column: 1;
      grid-row: 1;
    }
    & > :nth-child(2) {
      grid-column: 1;
      grid-row: 2;
      justify-self: start;
    }
    & > :nth-child(3) {
      grid-column: 2;
      grid-row: 2;
      flex-direction: row;
      align-items: baseline;
      gap: 4px;
      justify-self: end;
    }
    & > :nth-child(4) {
      grid-column: 2;
      grid-row: 1;
    }
  }
`;

/* ─────────────────────────────────────────────────────────
   Add Jobs modal
───────────────────────────────────────────────────────── */
const ModalBackdrop = styled.div`
  /* Portaled to document.body — outside <Shell>, so re-declare the theme
     tokens here or var(--surface)/var(--ink) resolve to nothing and the
     modal renders transparent. */
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(2px);
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 40px;
  /* 40px a side left a phone ~295px of modal. */
  @media (max-width: 640px) {
    padding: 12px;
  }
`;
const Modal = styled.div`
  background: var(--surface);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 80px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  color: var(--ink);
  /* dvh so the footer clears a phone's browser toolbar. */
  @media (max-width: 640px) {
    max-height: calc(100dvh - 24px);
  }
`;
const ModalHead = styled.div`
  padding: 20px 24px 14px;
  border-bottom: 1px solid var(--line-soft);
  @media (max-width: 640px) {
    padding: 16px 16px 12px;
  }
`;
const ModalH = styled.div`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 400;
`;
const ModalSub = styled.div`
  font-size: 13px;
  color: var(--ink-muted);
  margin-top: 4px;
`;
const ModalBody = styled.div`
  padding: 18px 24px;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Fixed height so every tab/phase renders in an identical container —
     taller content scrolls, and the flex parent still shrinks it on short
     viewports (overflow:auto gives it min-height:0). */
  height: 420px;
  @media (max-width: 640px) {
    padding: 16px;
  }
`;
const ModalFoot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid var(--line-soft);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  background: var(--surface-tint);
  /* Count + Cancel + a long primary label overran a phone-width footer. */
  @media (max-width: 640px) {
    padding: 12px 16px;
    flex-wrap: wrap;
  }
`;
/* Confirmation step of the industry picker: what's being added / removed,
   what it costs, and the three rules, laid out as a card rather than a banner. */
const ConfirmCard = styled.div`
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
  overflow: hidden;
  margin-bottom: 14px;
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid var(--line-soft);
  }
  .title {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
  }
  .total {
    font-size: 13px;
    color: var(--ink-muted);
    white-space: nowrap;
  }
  .total b {
    font-size: 15px;
    font-weight: 700;
    color: var(--ink);
    margin-right: 4px;
  }
  .group {
    padding: 12px 16px 12px 15px;
    border-left: 3px solid var(--line-strong);
  }
  .group + .group {
    border-top: 1px solid var(--line-soft);
  }
  .group.add {
    border-left-color: var(--success);
    background: color-mix(in srgb, var(--success-soft) 55%, var(--surface));
  }
  .group.remove {
    border-left-color: var(--danger);
    background: color-mix(in srgb, var(--danger-soft) 45%, var(--surface));
  }
  .label {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 8px;
  }
  .label .meta {
    font-weight: 500;
    color: var(--ink-muted);
    white-space: nowrap;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--surface);
    border: 1px solid var(--line);
    font-size: 12px;
    color: var(--ink-soft);
  }
  .chip.more {
    color: var(--ink-muted);
    border-style: dashed;
  }
  .fine {
    margin: 0;
    padding: 11px 16px 12px 30px;
    border-top: 1px solid var(--line-soft);
    font-size: 12px;
    line-height: 1.5;
    color: var(--ink-muted);
  }
  .fine li + li {
    margin-top: 2px;
  }
  .fine b {
    font-weight: 600;
    color: var(--ink-soft);
  }
`;

const FootCount = styled.span`
  font-size: 12.5px;
  color: var(--ink-muted);
  margin-right: auto;
  b {
    color: var(--ink);
    font-weight: 600;
  }
`;
const Dropzone = styled.div`
  border: 2px dashed var(--line-strong);
  border-radius: 14px;
  padding: 30px 20px 24px;
  text-align: center;
  background: var(--surface-tint);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  &:hover {
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--bg-2);
    color: var(--ink-soft);
    display: grid;
    place-items: center;
    margin-bottom: 8px;
  }
  &:hover .icon {
    background: var(--accent);
    color: var(--on-fill);
  }
  .h {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--ink);
  }
  .sub {
    font-size: 12.5px;
    color: var(--ink-muted);
    max-width: 320px;
    margin: 2px auto 10px;
    line-height: 1.45;
  }
`;
const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  font-size: 12px;
  color: var(--ink-muted);
`;
const PhaseLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
  padding: 48px 20px;
  .h {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    margin-top: 10px;
  }
  .sub {
    font-size: 12.5px;
    color: var(--ink-muted);
  }
  .spin {
    animation: ${spin} 1s linear infinite;
    color: var(--accent);
  }
`;
const ReviewBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: var(--surface-tint);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 13px 15px;
  margin-bottom: 14px;
  font-size: 13.5px;
  color: var(--ink);
  /* The text column takes the leftover width and wraps; min-width:0 +
     overflow-wrap let a long error message (or an unbroken URL) break
     instead of forcing the row wider than the banner. */
  & > div {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  /* Action button ("Try another" / "Edit") never shrinks or clips — it
     keeps its full width on one line no matter how long the message is. */
  button {
    flex-shrink: 0;
  }
  b {
    font-weight: 600;
  }
  .sub {
    font-size: 12px;
    color: var(--ink-muted);
    font-weight: 400;
    margin-top: 2px;
  }
  .review-badge {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    flex-shrink: 0;
    background: var(--success-soft);
    color: var(--success);
    display: grid;
    place-items: center;
  }
  .firm-badge {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    flex-shrink: 0;
    color: var(--on-fill);
    font-weight: 700;
    font-size: 13px;
    display: grid;
    place-items: center;
  }
`;
const MapList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
`;
const MapRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 9px 12px;
  .field {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
  }
  .field .req {
    color: var(--accent);
    margin-left: 3px;
  }
  .sample {
    font-size: 11px;
    color: var(--ink-muted);
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  select {
    width: 100%;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--line-strong);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    padding: 0 8px;
    outline: 0;
  }
  select:focus {
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
`;
const ScoutInput = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  padding: 0 12px;
  height: 38px;
  background: var(--surface);
  transition: border-color 0.12s, box-shadow 0.12s;
  &:focus-within {
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
  .lead {
    color: var(--ink-muted);
    flex-shrink: 0;
  }
  input {
    border: 0;
    outline: 0;
    background: transparent;
    flex: 1;
    font: inherit;
    font-size: 14px;
    color: var(--ink);
  }
`;
const IndustryCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--surface);
  font: inherit;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
  &:hover {
    border-color: var(--line-strong);
    background: var(--surface-tint);
  }
  .icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: var(--accent-soft);
    color: var(--accent);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .h {
    font-weight: 600;
    font-size: 14px;
  }
  .sub {
    font-size: 12.5px;
    color: var(--ink-muted);
    margin-top: 2px;
  }
  .picked {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    white-space: nowrap;
  }
`;
const FirmToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  .spacer {
    flex: 1;
  }
`;
const FirmList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const FirmRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink);
  &:hover {
    background: var(--surface-tint);
  }
  input {
    accent-color: var(--accent);
    width: 15px;
    height: 15px;
    margin: 0;
    flex-shrink: 0;
  }
  /* The global 44px input floor made every firm row ~60px tall. */
  @media (max-width: 450px) {
    input {
      min-height: 0;
    }
  }
  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .n {
    font-size: 12px;
    color: var(--ink-muted);
    flex-shrink: 0;
  }
`;
const LinkBtn = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
// Source tiles (careers page + job boards), the worldwide note and the URL input on the Scout tab.
const PlatformPick = styled.div`
  margin-bottom: 12px;
  .boxes {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .box .globe {
    color: var(--accent);
  }
  .scope {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 12px;
    color: var(--ink-muted);
  }
  .scope b {
    font-weight: 600;
    color: var(--ink);
  }
  .url {
    margin-top: 10px;
  }
  .box {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 12px 7px 9px;
    border: 1px solid var(--line-strong);
    border-radius: 9px;
    background: var(--surface);
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
  }
  .box:hover {
    border-color: var(--ink-3);
  }
  .box[data-on="true"] {
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .box input {
    accent-color: var(--accent);
    width: 15px;
    height: 15px;
    margin: 0;
  }
  .cands {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
  }
  .cands {
    margin-top: 14px;
    padding: 14px 16px;
    border: 1px solid var(--line);
    border-radius: 11px;
    background: var(--surface-2, var(--surface));
    display: grid;
    gap: 14px;
  }
  .cands .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    color: var(--ink-muted);
  }
  .cands .head button {
    flex-shrink: 0;
  }
  .cands .head .spin {
    animation: ${spin} 1s linear infinite;
  }
  .cand {
    display: grid;
    grid-template-columns: 170px 1fr auto;
    align-items: center;
    gap: 14px;
  }
  .cand .view {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: var(--accent);
    white-space: nowrap;
    text-decoration: none;
  }
  .cand .view:hover {
    text-decoration: underline;
  }
  .cand button.view {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .cand .view.off {
    color: var(--ink-faint);
    cursor: default;
    text-decoration: none;
  }
  /* A board with no result: one muted line instead of an empty dropdown. */
  .cand .miss {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 34px;
    font-size: 13px;
    color: var(--ink-muted);
  }
  .cand .miss.warn {
    color: var(--warn);
  }
  .cand .pop {
    position: relative;
  }
  .cand .lbl {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
  }
  .cand select {
    width: 100%;
    min-width: 0;
    height: 38px;
    border-radius: 9px;
    border: 1px solid var(--line-strong);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    padding: 0 10px;
    outline: 0;
  }
  .cand select:focus {
    border-color: var(--ink-3);
    box-shadow: 0 0 0 3px var(--ink-ring);
  }
  @media (max-width: 640px) {
    .cand {
      grid-template-columns: 1fr;
      gap: 6px;
    }
    /* Hint text takes its own line, the Find button drops below it, instead
       of squeezing the hint into a narrow column. */
    .cands .head {
      flex-wrap: wrap;
    }
    .cands {
      padding: 12px;
    }
  }
  /* Checkbox tiles and the "View jobs" link are text-sized; the global 44px
     floor on inputs/links stretched the tiles and spaced out each board row. */
  @media (max-width: 450px) {
    .box input,
    .cand .view {
      min-height: 0;
    }
    .cand .view {
      justify-self: start;
    }
  }
`;
const ScoutHint = styled.div`
  display: flex;
  gap: 11px;
  align-items: flex-start;
  background: var(--accent-soft);
  border-radius: 11px;
  padding: 13px 15px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--accent-ink);
  margin-top: 12px;
  b {
    font-weight: 600;
  }
`;
const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  color: var(--ink-soft);
  font-weight: 500;
  margin-bottom: 5px;
`;
const CompanyMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 50;
  max-height: 248px;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  padding: 4px;
`;
const CompanyOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 0;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  font: inherit;
  color: var(--ink);
  background: ${(p) => (p.$active ? "var(--surface-tint)" : "none")};
  img {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    object-fit: contain;
    flex-shrink: 0;
    background: var(--surface-tint);
  }
  .nm {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dm {
    color: var(--ink-muted);
    font-size: 12px;
    margin-left: auto;
    flex-shrink: 0;
  }
  &:hover {
    background: var(--surface-tint);
  }
`;
const FieldTabs = styled.div`
  display: inline-flex;
  background: var(--bg-2);
  border-radius: 999px;
  padding: 3px;
  margin-top: 14px;
  /* Three tabs outrun a phone-width modal; scroll the strip, not the page. */
  @media (max-width: 450px) {
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/* ─────────────────────────────────────────────────────────
   Toast
───────────────────────────────────────────────────────── */
const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2500;
  background: var(--ink);
  color: var(--bg);
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: var(--shadow-lg);
  display: flex;
  gap: 8px;
  align-items: center;
  /* Phones: a full-width bar with even gutters rather than a box hugging the
     right edge that grows leftward to the screen edge on long messages. */
  @media (max-width: 450px) {
    left: 12px;
    right: 12px;
    bottom: 16px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
export const POSTED_OPTIONS = [
  { value: "0", label: "Today" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "365", label: "This year" },
];

// Seniority is a progression, not an alphabet — filter-options sorts A-Z, which
// reads Entry / Executive / Mid / Senior. Must match SENIORITY_LIST in
// jobs_scrapper/utils/classifier_ai.py.
const SENIORITY_ORDER = [
  "Entry-Level",
  "Mid-Level",
  "Senior Roles",
  "Executive",
];
// Unknown values sort last, keeping the server's A-Z order among themselves.
export const seniorityRank = (value) => {
  const i = SENIORITY_ORDER.indexOf(value);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

function stripHtml(html) {
  if (!html) return "";
  if (typeof window === "undefined") {
    return String(html)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const d = document.createElement("div");
  // Pad tag boundaries so textContent doesn't glue adjacent blocks together ("offerWe").
  d.innerHTML = String(html)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/></g, "> <");
  return (d.textContent || d.innerText || "").replace(/\s+/g, " ").trim();
}
function snippetOf(html, max = 180) {
  const text = stripHtml(html);
  if (text.length <= max) return text;
  const sliced = text.slice(0, max);
  const lastSpace = sliced.lastIndexOf(" ");
  return (lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced) + "…";
}
/* ─────────────────────────────────────────────────────────
   Dropdown filter (multi-select via toggles)
───────────────────────────────────────────────────────── */
export function FilterDropdown({
  label,
  values,
  options,
  onChange,
  icon,
  single = false,
  block = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const list = Array.isArray(values) ? values : values ? [values] : [];
  const active = list.length > 0;
  const summary = block
    ? !active
      ? `All ${label.toLowerCase()}`
      : list.length === 1
        ? list[0]
        : `${list.length} selected`
    : !active
      ? label
      : list.length === 1
        ? list[0]
        : `${label} · ${list.length}`;

  function toggle(opt) {
    if (single) {
      onChange(opt === list[0] ? [] : [opt]);
      setOpen(false);
      return;
    }
    const next = list.includes(opt)
      ? list.filter((v) => v !== opt)
      : [...list, opt];
    onChange(next);
  }

  const Trigger = block ? BlockTrigger : FilterTrigger;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Trigger
        $active={active}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={active ? summary : undefined}
      >
        {!block && icon}
        <TriggerText style={block ? { textAlign: "left" } : undefined}>
          {summary}
        </TriggerText>
        <TriggerCaret size={block ? 14 : 11} $open={open} />
      </Trigger>
      {open && (
        <Menu style={block ? { width: "100%", minWidth: 0 } : undefined}>
          <MenuScroll>
            <MenuItem
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
            >
              All {label.toLowerCase()}
              {!active && <FiCheck size={14} style={{ marginLeft: "auto" }} />}
            </MenuItem>
            <MenuDivider />
            {options.length === 0 && (
              <MenuItem
                style={{ color: "var(--ink-muted)", cursor: "default" }}
              >
                No options
              </MenuItem>
            )}
            {options.map((o) => (
              <MenuItem key={o} onClick={() => toggle(o)}>
                {o}
                {list.includes(o) && (
                  <FiCheck size={14} style={{ marginLeft: "auto" }} />
                )}
              </MenuItem>
            ))}
          </MenuScroll>
        </Menu>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Filters popover: one button that opens a panel of all filters
───────────────────────────────────────────────────────── */
export function FiltersPopover({ activeCount, alignEnd = false, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <FiltersButton
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <FiSliders size={14} />
        Filters
        {activeCount > 0 && <FilterCount>{activeCount}</FilterCount>}
        <TriggerCaret size={13} $open={open} />
      </FiltersButton>
      {open && (
        <FilterPanel $alignEnd={alignEnd}>
          <PanelTitle>Filters</PanelTitle>
          {children}
        </FilterPanel>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Job card (grid)
───────────────────────────────────────────────────────── */
export function JobGridCard({
  job,
  saved,
  onOpen,
  onViewDetails,
  onToggleSave,
  picked,
  pickerMode,
}) {
  const company = firstCompany(job.source);
  const title = job.title || job.job_title || "Untitled role";
  const snippet = useMemo(() => snippetOf(job.description), [job.description]);
  const rel = postedRel(job.posted_date);
  const loc = locationShort(job);
  const subCat = job.seniority || "";
  const mainCat = job.function || "";
  // Closed postings only ever reach here from saved jobs. They stay fully
  // openable — the pipeline attached to them (candidates, HR) outlives the
  // listing — so this is styling only.
  const closed = !!job.closed;
  return (
    <JCard
      $picked={picked}
      $closed={closed}
      onClick={() => onOpen(job)}
    >
      <JCardStar
        onClick={(e) => {
          e.stopPropagation();
          onToggleSave(job);
        }}
        title={saved ? "Remove from saved" : "Save job"}
      >
        {saved ? (
          <StarFilled size={14} style={{ color: "var(--star)" }} />
        ) : (
          <FiStar size={14} />
        )}
      </JCardStar>

      <JCardHead>
        <CompanyLogo name={company} size={40} radius={8} bg={companyColor(company)} fallback={initialOf(company)} />
        <div style={{ minWidth: 0 }}>
          <JCompany>
            <span className="nm">{company || "—"}</span>
            <PlatformMark platform={job.platform} />
          </JCompany>
          {mainCat && <JCompanyType>{mainCat}</JCompanyType>}
        </div>
      </JCardHead>

      <JTitle>{title}</JTitle>

      {snippet && <JSnippet>{snippet}</JSnippet>}

      <JMeta>
        <span className="item">
          <FiMapPin size={12} /> {loc}
        </span>
        {rel && (
          <span className="item">
            <FiClock size={12} /> {rel}
          </span>
        )}
      </JMeta>

      <JPills>
        {closed && <Tag $closed>Closed</Tag>}
        {mainCat && <Tag>{mainCat}</Tag>}
        {subCat && <Tag>{subCat}</Tag>}
      </JPills>

      <JFoot>
        <Btn
          $ghost
          $sm
          onClick={(e) => {
            e.stopPropagation();
            (onViewDetails || onOpen)(job);
          }}
        >
          View details <FiChevronRight size={12} />
        </Btn>
        {pickerMode && picked && (
          <Tag $secondary>
            <FiCheck size={12} /> Selected
          </Tag>
        )}
      </JFoot>
    </JCard>
  );
}

// Job row (list layout): same props as JobGridCard, swapped in by the layout toggle.
export const JobRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const JRow = styled.div`
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.06s;
  &:hover {
    border-color: var(--line-strong);
    box-shadow: var(--shadow-sm);
  }
  &:active {
    transform: translateY(0.5px);
  }
  ${({ $picked }) =>
    $picked &&
    css`
      border-color: var(--secondary);
      box-shadow: 0 0 0 1.5px var(--secondary), 0 0 0 5px var(--secondary-soft);
      transition: none;
      &:hover {
        border-color: var(--secondary);
      }
    `}
  ${({ $closed }) =>
    $closed &&
    css`
      border-style: dashed;
      background: var(--bg-2);
    `}
  @media (max-width: 640px) {
    padding: 12px 14px;
    gap: 11px;
  }
`;
const JRowMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;
const JRowTitle = styled.h3`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 18px;
  line-height: 1.25;
  font-weight: 400;
  color: var(--ink);
  letter-spacing: -0.005em;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const JRowSub = styled.div`
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-muted);
  white-space: nowrap;
  overflow: hidden;
  .co {
    font-weight: 600;
    color: var(--ink-soft);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dot {
    color: var(--ink-faint);
  }
`;
// Tags are the first thing to go when the row gets tight — title,
// company and the actions matter more at narrow widths.
const JRowTags = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  @media (max-width: 1040px) {
    display: none;
  }
`;
const JRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;
const JRowStar = styled.button`
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--ink-muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, color 0.12s;
  &:hover {
    background: var(--bg-2);
    color: var(--ink);
  }
  @media (max-width: 450px) {
    min-height: 0;
  }
`;
const JRowDetailsBtn = styled(Btn)`
  @media (max-width: 640px) {
    display: none;
  }
`;

export function JobRowCard({
  job,
  saved,
  onOpen,
  onViewDetails,
  onToggleSave,
  picked,
  pickerMode,
}) {
  const company = firstCompany(job.source);
  const title = job.title || job.job_title || "Untitled role";
  const rel = postedRel(job.posted_date);
  const loc = locationShort(job);
  const subCat = job.seniority || "";
  const mainCat = job.function || "";
  const closed = !!job.closed;
  return (
    <JRow $picked={picked} $closed={closed} onClick={() => onOpen(job)}>
      <CompanyLogo name={company} size={38} radius={8} bg={companyColor(company)} fallback={initialOf(company)} />

      <JRowMain>
        <JRowTitle>{title}</JRowTitle>
        <JRowSub>
          <span className="co">{company || "—"}</span>
          <PlatformMark platform={job.platform} />
          <span className="dot">·</span>
          <span className="item">
            <FiMapPin size={12} /> {loc}
          </span>
          {rel && (
            <>
              <span className="dot">·</span>
              <span className="item">
                <FiClock size={12} /> {rel}
              </span>
            </>
          )}
        </JRowSub>
      </JRowMain>

      <JRowTags>
        {closed && <Tag $closed>Closed</Tag>}
        {mainCat && <Tag>{mainCat}</Tag>}
        {subCat && <Tag>{subCat}</Tag>}
      </JRowTags>

      <JRowActions>
        {pickerMode && picked && (
          <Tag $secondary>
            <FiCheck size={12} /> Selected
          </Tag>
        )}
        <JRowDetailsBtn
          $ghost
          $sm
          onClick={(e) => {
            e.stopPropagation();
            (onViewDetails || onOpen)(job);
          }}
        >
          View details <FiChevronRight size={12} />
        </JRowDetailsBtn>
        {onToggleSave && (
          <JRowStar
            onClick={(e) => {
              e.stopPropagation();
              onToggleSave(job);
            }}
            title={saved ? "Remove from saved" : "Save job"}
          >
            {saved ? (
              <StarFilled size={14} style={{ color: "var(--star)" }} />
            ) : (
              <FiStar size={14} />
            )}
          </JRowStar>
        )}
      </JRowActions>
    </JRow>
  );
}

// Row-shaped skeleton — mirrors JRow's height so the list reserves
// identical space while loading.
export function JobRowSkeleton() {
  return (
    <JRow as="div" style={{ cursor: "default" }} aria-hidden="true">
      <Skeleton width={38} height={38} radius="11px" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Skeleton width="38%" height={18} mb={6} />
        <Skeleton width="26%" height={12} />
      </div>
      <Skeleton width={88} height={22} radius="6px" />
      <Skeleton width={106} height={30} radius="8px" />
    </JRow>
  );
}
function JobListSkeleton() {
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 6 });
  return (
    <JobRows ref={ref} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <JobRowSkeleton key={i} />
      ))}
    </JobRows>
  );
}

// Boxes ↔ rows toggle: one persisted preference for every job list and Saved jobs.
const JOB_LAYOUT_KEY = "jobs.layout";

function readJobLayout() {
  try {
    return window.localStorage.getItem(JOB_LAYOUT_KEY) === "rows"
      ? "rows"
      : "grid";
  } catch {
    return "grid";
  }
}

export function useJobLayout() {
  const [layout, setLayout] = useState(readJobLayout);
  const change = useCallback((next) => {
    setLayout(next);
    try {
      window.localStorage.setItem(JOB_LAYOUT_KEY, next);
    } catch {
      /* private mode / storage disabled — session-only preference */
    }
  }, []);
  return [layout, change];
}

const LayoutSwitch = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface-tint);
  padding: 2px;
  gap: 2px;
  flex: 0 0 auto;
`;
const LayoutSwitchBtn = styled.button`
  width: 30px;
  height: 26px;
  border: 0;
  background: transparent;
  color: var(--ink-muted);
  border-radius: 6px;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, color 0.12s;
  &:hover {
    color: var(--ink);
  }
  ${({ $active }) =>
    $active &&
    css`
      background: var(--surface);
      color: var(--ink);
      box-shadow: var(--shadow-sm);
    `}
  /* Segmented icon toggle: the 44px floor made it ~48px tall beside a
     13px count line. */
  @media (max-width: 450px) {
    min-height: 0;
  }
`;

export function JobLayoutToggle({ value, onChange }) {
  return (
    <LayoutSwitch role="group" aria-label="Job layout">
      <LayoutSwitchBtn
        type="button"
        $active={value === "grid"}
        aria-pressed={value === "grid"}
        title="Box view"
        onClick={() => onChange("grid")}
      >
        <FiGrid size={14} />
      </LayoutSwitchBtn>
      <LayoutSwitchBtn
        type="button"
        $active={value === "rows"}
        aria-pressed={value === "rows"}
        title="Row view"
        onClick={() => onChange("rows")}
      >
        <FiList size={14} />
      </LayoutSwitchBtn>
    </LayoutSwitch>
  );
}

/* ─────────────────────────────────────────────────────────
   Job detail view (inline)
───────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────
   Tracked Companies (localStorage-only fake)
───────────────────────────────────────────────────────── */
// Company-name field with Brandfetch autocomplete (debounced /api/companies/search).
function CompanyTypeahead({ value, onChange, onEnter }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const skipRef = useRef(false); // suppress the fetch right after a pick
  // The menu is portalled to <body> (see below), so it needs the field's viewport rect.
  const [rect, setRect] = useState(null);

  const syncRect = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    syncRect();
    // Capture phase catches scrolling of the modal body / row list, not just the window.
    window.addEventListener("scroll", syncRect, true);
    window.addEventListener("resize", syncRect);
    return () => {
      window.removeEventListener("scroll", syncRect, true);
      window.removeEventListener("resize", syncRect);
    };
  }, [open, syncRect]);

  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    const q = (value || "").trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetchWithAuth(
          `/api/companies/search?q=${encodeURIComponent(q)}`
        );
        const data = await res.json();
        if (cancelled) return;
        setResults(Array.isArray(data) ? data : []);
        setActive(-1);
        setOpen(true);
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]);

  useEffect(() => {
    function onDoc(e) {
      const inField = wrapRef.current && wrapRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inField && !inMenu) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(b) {
    skipRef.current = true;
    onChange(b.name, b.domain);
    setOpen(false);
    setResults([]);
  }

  function onKeyDown(e) {
    if (open && results.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % results.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i - 1 + results.length) % results.length);
        return;
      }
      if (e.key === "Enter" && active >= 0) {
        e.preventDefault();
        pick(results[active]);
        return;
      }
    }
    if (e.key === "Enter") onEnter && onEnter();
    if (e.key === "Escape") setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <ScoutInput>
        <FiBriefcase size={14} className="lead" />
        <input
          placeholder="e.g. Stone King"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </ScoutInput>
      {open &&
        results.length > 0 &&
        rect &&
        createPortal(
          // Portalled to <body>: an `overflow` ancestor (the modal body) would
          // otherwise clip an absolutely-positioned menu.
          <CompanyMenu
            ref={menuRef}
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              right: "auto",
              width: rect.width,
              // Above the modal backdrop (2000), below the toast layer (2500).
              zIndex: 2100,
            }}
          >
            {results.map((b, i) => (
              <CompanyOption
                key={`${b.domain}-${i}`}
                type="button"
                $active={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(b)}
              >
                {b.icon ? <img src={b.icon} alt="" /> : <FiBriefcase size={16} />}
                <span className="nm">{b.name}</span>
                <span className="dm">{b.domain}</span>
              </CompanyOption>
            ))}
          </CompanyMenu>,
          document.body,
        )}
    </div>
  );
}

// Relative "x ago" for the tracked-companies last-checked column.
function relTimeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function prettyAts(a) {
  return a ? String(a).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";
}
// Two missed daily runs before a company counts as unreadable rather than checked.
const STALE_AFTER_MS = 48 * 60 * 60 * 1000;

// Four states, all from what the user can observe: has it ever been checked, did
// we manage to check it lately, and did it turn up roles. Scrape internals play
// no part — a site we keep failing to read reads the same as one we never could.
function trackedStatus(r) {
  // No careers page: the job boards are the only sources, so read their state.
  if (!r.careersUrl) {
    const ps = Object.values(r.platforms || {});
    if (ps.some((p) => p.status === "queued")) return "syncing";
    if (ps.length && ps.every((p) => p.status === "failed")) return "unreadable";
    return (r.totalJobCount || 0) > 0 ? "active" : "empty";
  }
  if (!r.lastCheckedAt) return r.firstCheckFailed ? "unreadable" : "syncing";
  if (Date.now() - new Date(r.lastCheckedAt).getTime() > STALE_AFTER_MS)
    return "unreadable";
  return (r.jobCount || 0) > 0 ? "active" : "empty";
}

// Sub-line under the status pill: when we last looked, and what it turned up.
function checkedLine(c) {
  if (c.status === "unreadable") return "Will retry again in 24h";
  if (!c.lastChecked) return "Finding roles — this takes a minute";
  return (
    <>
      Last checked {c.lastChecked}
      {c.newJobs > 0 && <> · <b>{c.newJobs} new</b> this week</>}
    </>
  );
}

// Why the jobs count isn't clickable.
function viewHint(status) {
  if (status === "syncing") return "Roles will appear once the first check finishes";
  if (status === "unreadable") return "We couldn't read this careers site";
  return "No roles found on the last check";
}

// One pill for all three tracked rows (company, per-link, group header).
function TrackStatusPill({ status, daily }) {
  if (status === "syncing") {
    return (
      <StatusPill $type="syncing">
        <FiRefreshCw size={12} className="spin" /> Setting up
      </StatusPill>
    );
  }
  const label =
    status === "unreadable"
      ? "Couldn't read this site"
      : status === "empty"
        ? "No roles found"
        : daily
          ? "Active · daily"
          : "Active";
  return (
    <StatusPill $type={status}>
      <span className="dot" /> {label}
    </StatusPill>
  );
}

// Map an /api/scout/tracked row to the tracked view's shape; scrape internals (confidence, errors) stay out.
function mapTrackedRow(r) {
  const status = trackedStatus(r);
  // Job boards configured on this row: { linkedin: { country, enabled, matchedName, jobCount, status, ... } }
  const platforms = r.platforms && typeof r.platforms === "object" ? r.platforms : {};
  // Rows without a careers page take "last checked" from their most recent board run.
  const lastCheckedAt =
    r.careersUrl
      ? r.lastCheckedAt
      : Object.values(platforms)
          .map((p) => p.lastCheckedAt)
          .filter(Boolean)
          .sort()
          .pop() || null;
  return {
    id: r.id,
    name: r.companyName,
    careersUrl: r.careersUrl || null,
    ats: prettyAts(r.ats),
    type: status === "syncing" ? "Setting up" : "Checks daily",
    status,
    // Careers-page bucket (the per-link sub-row) vs every bucket (the company row).
    jobsAdded: r.jobCount || 0,
    totalJobs: Number.isFinite(r.totalJobCount) ? r.totalJobCount : r.jobCount || 0,
    newJobs: r.newCount || 0,
    lastChecked: relTimeAgo(lastCheckedAt),
    platforms,
  };
}

// Platform sub-row pill: the worker's status → the pill type/label the careers rows use.
const PLATFORM_PILL = {
  queued: { type: "syncing", label: "Setting up" },
  ok: { type: "active", label: "Active · daily" },
  failed: { type: "unreadable", label: "Couldn't pull jobs" },
  unlisted: { type: "empty", label: "Not listed" },
  pending: { type: "empty", label: "Pending" },
};

// One entry per company: rows are keyed by careers URL, so a company tracked twice arrives as two rows.
function groupTrackedByCompany(list) {
  const groups = new Map();
  for (const c of list) {
    const key = c.name || "—";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  return Array.from(groups.values()).map((links) => ({
    name: links[0].name,
    links,
    totalJobs: links.reduce((s, l) => s + (Number(l.totalJobs) || 0), 0),
    anySyncing: links.some((l) => l.status === "syncing"),
    // Nothing under this company could be read on the last round.
    allUnreadable: links.every((l) => l.status === "unreadable"),
    totalNew: links.reduce((s, l) => s + (Number(l.newJobs) || 0), 0),
  }));
}

function TrackRowItem({ c, onRemove, onViewJobs, onTogglePlatform }) {
  const [menu, setMenu] = useState(false);
  // Job-board sub-rows collapse under the company row, like a multi-link group.
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);
  // Nothing to open while the first scrape runs, or when nothing was found.
  const canView = c.status !== "syncing" && (Number(c.totalJobs) || 0) > 0;
  const platformRows = platformSubRows([c], onTogglePlatform);
  const collapsible = platformRows.length > 0;
  const sources = (c.careersUrl ? 1 : 0) + platformRows.length;
  const row = (
    <TrackRow
      style={collapsible ? { cursor: "pointer" } : undefined}
      onClick={collapsible ? () => setOpen((v) => !v) : undefined}
    >
      <CompanyLogo name={c.name} size={40} radius={10} bg={companyColor(c.name)} fallback={initialOf(c.name)} />
      <div>
        <TrackName>{c.name}</TrackName>
        <TrackType>
          {c.type}
          {collapsible && <> · {sources} sources · {open ? "hide" : "show"} details</>}
        </TrackType>
      </div>
      <StatusCell>
        <TrackStatusPill status={c.status} daily />
        <TrackChecked>{checkedLine(c)}</TrackChecked>
      </StatusCell>
      <TrackStat
        $clickable={canView}
        onClick={
          canView
            ? (e) => {
                e.stopPropagation();
                onViewJobs(c.name);
              }
            : undefined
        }
        title={canView ? "View these jobs" : viewHint(c.status)}
      >
        <span className="num">{c.totalJobs || 0}</span>
        <span className="lbl">jobs</span>
      </TrackStat>
      <TrackActions onClick={(e) => e.stopPropagation()}>
        <div ref={menuRef} style={{ position: "relative" }}>
          <IconBtn
            onClick={(e) => {
              e.stopPropagation();
              setMenu((v) => !v);
            }}
          >
            <FiMoreHorizontal size={16} />
          </IconBtn>
          {menu && (
            <Menu style={{ left: "auto", right: 0, minWidth: 180 }}>
              <MenuScroll>
                <MenuItem
                  onClick={canView ? () => onViewJobs(c.name) : undefined}
                  style={canView ? undefined : { opacity: 0.45, cursor: "not-allowed" }}
                >
                  <FiBriefcase size={14} /> View jobs
                </MenuItem>
                {c.careersUrl && (
                  <MenuItem
                    as="a"
                    href={c.careersUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiExternalLink size={14} /> Open careers site
                  </MenuItem>
                )}
                <MenuDivider />
                <MenuItem
                  style={{ color: "var(--danger)" }}
                  onClick={() => onRemove(c.id)}
                >
                  <FiTrash2 size={14} /> Stop tracking
                </MenuItem>
              </MenuScroll>
            </Menu>
          )}
        </div>
        {collapsible && (
          <Chevron $open={open} onClick={() => setOpen((v) => !v)}>
            <FiChevronDown size={16} />
          </Chevron>
        )}
      </TrackActions>
    </TrackRow>
  );
  if (!collapsible) return row;
  return (
    <TrackGroupWrap>
      {row}
      {open && (
        <TrackSubList>
          {c.careersUrl && <TrackLinkSubRow link={c} />}
          {platformRows}
        </TrackSubList>
      )}
    </TrackGroupWrap>
  );
}

// One tracked careers URL inside an expanded company. `onRemove` only when the site is one
// link among several; a company's single careers row is dropped from the company menu instead.
function TrackLinkSubRow({ link, onRemove }) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);
  return (
    <TrackSubRow>
      <div className="link-main">
        <a
          className="host"
          href={link.careersUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={link.careersUrl}
        >
          <FiGlobe size={12} /> Official Career Site
          <FiExternalLink size={12} />
        </a>
        <div className="sub">{checkedLine(link)}</div>
      </div>
      <TrackStatusPill status={link.status} />
      <div className="sub-stat">
        <span className="num">{link.jobsAdded || 0}</span>
        <span className="lbl">jobs</span>
      </div>
      <div ref={menuRef} style={{ position: "relative", justifySelf: "end" }}>
        <IconBtn
          onClick={(e) => {
            e.stopPropagation();
            setMenu((v) => !v);
          }}
        >
          <FiMoreHorizontal size={16} />
        </IconBtn>
        {menu && (
          <Menu style={{ left: "auto", right: 0, minWidth: 200 }}>
            <MenuScroll>
              <MenuItem
                as="a"
                href={link.careersUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiExternalLink size={14} /> Open careers site
              </MenuItem>
              {onRemove && (
                <>
                  <MenuDivider />
                  <MenuItem
                    style={{ color: "var(--danger)" }}
                    onClick={() => onRemove(link.id)}
                  >
                    <FiTrash2 size={14} /> Stop tracking this site
                  </MenuItem>
                </>
              )}
            </MenuScroll>
          </Menu>
        )}
      </div>
    </TrackSubRow>
  );
}

// One job board configured on a tracked row (shared scrape; the toggle is per user).
// What the sub-line says: a fact about the last run, never how the scrape went internally.
function platformLine(p, label, companyName) {
  if (p.status === "queued" || p.status === "pending") return "Finding roles — this takes a minute";
  if (p.status === "failed") return p.reason === "unlisted" ? `No ${label} listing for this company` : "Will retry again in 24h";
  const listedAs = p.matchedName && p.matchedName.trim().toLowerCase() !== (companyName || "").trim().toLowerCase();
  return (
    <>
      Last checked {relTimeAgo(p.lastCheckedAt)}
      {listedAs && <> · listed as {p.matchedName}</>}
    </>
  );
}

function TrackPlatformSubRow({ trackedId, platform, p, companyName, onToggle }) {
  const [menu, setMenu] = useState(false);
  const { features: planFeatures } = usePlan();
  // Turning a board back on needs Growth; turning one off is always allowed.
  const boardLocked = !p.enabled && !planFeatures.thirdPartyBoards;
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);
  const label = PLATFORMS[platform]?.label || platform;
  const pill = (p.status === "failed" && p.reason === "unlisted" ? PLATFORM_PILL.unlisted : PLATFORM_PILL[p.status]) || PLATFORM_PILL.pending;
  return (
    <TrackSubRow $off={!p.enabled}>
      <div className="link-main">
        {p.url ? (
          <a
            className="host"
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${label} in a new tab`}
            onClick={(e) => e.stopPropagation()}
          >
            <PlatformLogo platform={platform} size={16} /> {label}
            {p.country && p.country !== "Worldwide" && (
              <span className="ctry">· {p.country}</span>
            )}
            <FiExternalLink size={12} />
          </a>
        ) : (
          <span className="host" title={label}>
            <PlatformLogo platform={platform} size={16} /> {label}
            {p.country && p.country !== "Worldwide" && (
              <span className="ctry">· {p.country}</span>
            )}
          </span>
        )}
        <div className="sub">
          {platformLine(p, label, companyName)}
          {!p.enabled && <> · not pulling for you</>}
        </div>
      </div>
      <StatusPill $type={pill.type}>
        {pill.type === "syncing" ? (
          <FiRefreshCw size={12} className="spin" />
        ) : (
          <span className="dot" />
        )}{" "}
        {pill.label}
      </StatusPill>
      <div className="sub-stat">
        <span className="num">{p.jobCount || 0}</span>
        <span className="lbl">jobs</span>
      </div>
      <div ref={menuRef} style={{ position: "relative", justifySelf: "end" }}>
        <IconBtn
          onClick={(e) => {
            e.stopPropagation();
            setMenu((v) => !v);
          }}
        >
          <FiMoreHorizontal size={16} />
        </IconBtn>
        {menu && (
          <Menu style={{ left: "auto", right: 0, minWidth: 220 }}>
            <MenuScroll>
              <MenuItem
                style={p.enabled ? { color: "var(--danger)" } : undefined}
                onClick={() => {
                  setMenu(false);
                  if (boardLocked) return;
                  onToggle(trackedId, platform, !p.enabled);
                }}
              >
                {p.enabled ? (
                  <>
                    <FiX size={14} /> Stop pulling from {label}
                  </>
                ) : boardLocked ? (
                  <>
                    <FiRefreshCw size={14} /> Pull from {label} <PlanLock plan="Growth" label="Job boards" />
                  </>
                ) : (
                  <>
                    <FiRefreshCw size={14} /> Pull from {label}
                  </>
                )}
              </MenuItem>
            </MenuScroll>
          </Menu>
        )}
      </div>
    </TrackSubRow>
  );
}

// Every platform sub-row for a list of tracked links, after their careers-page rows.
function platformSubRows(links, onToggle) {
  return links.flatMap((l) =>
    Object.keys(l.platforms || {}).map((k) => (
      <TrackPlatformSubRow
        key={`${l.id}:${k}`}
        trackedId={l.id}
        platform={k}
        p={l.platforms[k]}
        companyName={l.name}
        onToggle={onToggle}
      />
    )),
  );
}

// A company with two or more tracked careers URLs: one collapsible header row
// that aggregates the links, expanding to a sub-row per link.
function TrackCompanyGroup({ group, onRemove, onViewJobs, onTogglePlatform }) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menu]);
  const status = group.anySyncing
    ? "syncing"
    : group.allUnreadable
      ? "unreadable"
      : group.totalJobs > 0
        ? "active"
        : "empty";
  const canView = !group.anySyncing && group.totalJobs > 0;
  return (
    <TrackGroupWrap>
      <TrackRow style={{ cursor: "pointer" }} onClick={() => setOpen((v) => !v)}>
        <CompanyLogo name={group.name} size={40} radius={10} bg={companyColor(group.name)} fallback={initialOf(group.name)} />
        <div>
          <TrackName>{group.name}</TrackName>
          <TrackType>
            {group.links.length} sources · {open ? "hide" : "show"} details
          </TrackType>
        </div>
        <StatusCell>
          <TrackStatusPill status={status} daily />
          <TrackChecked>
            {status === "unreadable" ? (
              "Will retry again in 24h"
            ) : (
              <>
                {group.links.length} tracked sources
                {group.totalNew > 0 && (
                  <> · <b>{group.totalNew} new</b> this week</>
                )}
              </>
            )}
          </TrackChecked>
        </StatusCell>
        <TrackStat
          $clickable={canView}
          onClick={
            canView
              ? (e) => {
                  e.stopPropagation();
                  onViewJobs(group.name);
                }
              : undefined
          }
          title={canView ? "View these jobs" : viewHint(status)}
        >
          <span className="num">{group.totalJobs || 0}</span>
          <span className="lbl">jobs</span>
        </TrackStat>
        <TrackActions onClick={(e) => e.stopPropagation()}>
          <div ref={menuRef} style={{ position: "relative" }}>
            <IconBtn
              onClick={(e) => {
                e.stopPropagation();
                setMenu((v) => !v);
              }}
            >
              <FiMoreHorizontal size={16} />
            </IconBtn>
            {menu && (
              <Menu style={{ left: "auto", right: 0, minWidth: 210 }}>
                <MenuScroll>
                  <MenuItem
                    onClick={canView ? () => onViewJobs(group.name) : undefined}
                    style={
                      canView
                        ? undefined
                        : { opacity: 0.45, cursor: "not-allowed" }
                    }
                  >
                    <FiBriefcase size={14} /> View jobs
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    style={{ color: "var(--danger)" }}
                    onClick={() => group.links.forEach((l) => onRemove(l.id))}
                  >
                    <FiTrash2 size={14} /> Stop tracking all {group.links.length}{" "}
                    sources
                  </MenuItem>
                </MenuScroll>
              </Menu>
            )}
          </div>
          <Chevron $open={open} onClick={() => setOpen((v) => !v)}>
            <FiChevronDown size={16} />
          </Chevron>
        </TrackActions>
      </TrackRow>
      {open && (
        <TrackSubList>
          {group.links.map(
            (l) => l.careersUrl && <TrackLinkSubRow key={l.id} link={l} onRemove={onRemove} />,
          )}
          {platformSubRows(group.links, onTogglePlatform)}
        </TrackSubList>
      )}
    </TrackGroupWrap>
  );
}

// Row-shaped skeleton — built from TrackRow's own cells so it keeps the real row's height.
function TrackRowSkeleton() {
  return (
    <TrackRow style={{ pointerEvents: "none" }}>
      <Skeleton width={40} height={40} radius="10px" />
      <div>
        <TrackName>
          <CountSkeleton width={150} height={13} />
        </TrackName>
        <TrackType>
          <CountSkeleton width={88} />
        </TrackType>
      </div>
      <StatusCell>
        <Skeleton width={104} height={24} radius="99px" />
        <TrackChecked>
          <CountSkeleton width={128} />
        </TrackChecked>
      </StatusCell>
      <TrackStat as="div">
        <span className="num">
          <CountSkeleton width={26} height={18} />
        </span>
        <span className="lbl">jobs</span>
      </TrackStat>
      <TrackActions>
        <Skeleton width={28} height={28} radius="8px" />
      </TrackActions>
    </TrackRow>
  );
}
function TrackListSkeleton() {
  const ref = useRef(null);
  const count = useFillCount(ref, { min: 4 });
  return (
    <TrackList ref={ref} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <TrackRowSkeleton key={i} />
      ))}
    </TrackList>
  );
}

function TrackedCompaniesView({
  list,
  industries = [],
  loading = false,
  onRemove,
  onAddCompany,
  onViewJobs,
  onTogglePlatform,
}) {
  // Firms picked by industry (corpus scraped nightly) render as ordinary rows; the id carries the pick.
  const rows = [
    ...list,
    ...industries.flatMap((ind) =>
      (ind.sources || []).map((f) => ({
        id: `industry:${ind.key}:${f.name}`,
        name: f.name,
        careersUrl: null,
        type: "Checks daily",
        status: (Number(f.jobCount) || 0) > 0 ? "active" : "empty",
        totalJobs: Number(f.jobCount) || 0,
        newJobs: 0,
        lastChecked: "today",
        platforms: {},
      })),
    ),
  ];
  const totalJobs = rows.reduce((sum, c) => sum + (Number(c.totalJobs) || 0), 0);
  const totalNew = rows.reduce((sum, c) => sum + (Number(c.newJobs) || 0), 0);
  const groups = groupTrackedByCompany(rows);
  const headRow = (
    <TrackHeadRow>
      <span style={{ gridColumn: "1 / 3" }}>Company</span>
      <span>Status</span>
      <span style={{ textAlign: "center" }}>Jobs</span>
      <span />
    </TrackHeadRow>
  );
  return (
    <TrackWrap>
      <TrackBanner>
        <span className="icon">
          <FiRefreshCw size={16} />
        </span>
        <div>
          <div className="h">Tracked companies sync every day</div>
          <div className="sub">
            We check each company's careers site daily and add new roles
            automatically. Only you can see these jobs.
          </div>
        </div>
        <Btn $primary className="cta" onClick={onAddCompany}>
          <FiPlus size={14} /> Track a company
        </Btn>
      </TrackBanner>
      {loading ? (
        <>
          <TrackSummary aria-hidden="true">
            <span>
              <CountSkeleton width={230} height={11} />
            </span>
          </TrackSummary>
          {headRow}
          <TrackListSkeleton />
        </>
      ) : (
        <FadeIn>
          <TrackSummary>
            <span>
              <b>{groups.length}</b> {groups.length === 1 ? "company" : "companies"}{" "}
              tracked
            </span>
            <span className="dot-sep">·</span>
            <span className="accent">
              <b>{totalJobs.toLocaleString()}</b> jobs imported
            </span>
            {totalNew > 0 && (
              <>
                <span className="dot-sep">·</span>
                <span className="accent">
                  <b>{totalNew.toLocaleString()}</b> new this week
                </span>
              </>
            )}
          </TrackSummary>
          {rows.length === 0 ? (
            <Empty style={{ padding: 70 }}>
              <div>
                <div className="art">
                  <FiBriefcase size={42} strokeWidth={1.4} />
                </div>
                <h3>No companies tracked yet</h3>
                <p>
                  Add a company and we'll check it daily, adding new roles as
                  they're posted.
                </p>
                <Btn $primary onClick={onAddCompany}>
                  <FiPlus size={14} /> Track a company
                </Btn>
              </div>
            </Empty>
          ) : (
            <>
              {headRow}
              <TrackList data-tour="jobs-tracklist">
                {groups.map((g) =>
                  g.links.length === 1 ? (
                    <TrackRowItem
                      key={g.links[0].id}
                      c={g.links[0]}
                      onRemove={onRemove}
                      onViewJobs={onViewJobs}
                      onTogglePlatform={onTogglePlatform}
                    />
                  ) : (
                    <TrackCompanyGroup
                      key={g.name}
                      group={g}
                      onRemove={onRemove}
                      onViewJobs={onViewJobs}
                      onTogglePlatform={onTogglePlatform}
                    />
                  ),
                )}
              </TrackList>
            </>
          )}
        </FadeIn>
      )}
    </TrackWrap>
  );
}

// "View jobs ▾" for a JobsDB pick that posts in several markets: one link per market.
function JobsdbMarketsMenu({ markets }) {
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenu(false);
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);
  return (
    <div className="pop" ref={menuRef}>
      <button type="button" className="view" onClick={() => setMenu((v) => !v)}>
        View jobs <FiChevronDown size={12} />
      </button>
      {menu && (
        <Menu style={{ left: "auto", right: 0, minWidth: 200 }}>
          <MenuScroll>
            {markets.map((m) => (
              <MenuItem
                key={m.url}
                as="a"
                href={m.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenu(false)}
              >
                <FiExternalLink size={14} /> {m.name} ({m.count})
              </MenuItem>
            ))}
          </MenuScroll>
        </Menu>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Add Jobs modal (Import + Scout)
───────────────────────────────────────────────────────── */
function AddJobsModal({
  open,
  importEnabled = true,
  // Scout by industry is parked (FEATURE_SCOUT_BY_INDUSTRY): no tab, picks only shrink via Stop tracking.
  industryEnabled = true,
  initialTab = "import",
  initialIndustry = "",
  onClose,
  onQueued,
  onImportFinished,
  onTrack,
  onIndustrySaved,
}) {
  const [tab, setTab] = useState(initialTab);
  // Import-from-file: idle → uploading → (excel) mapping → queued via onQueued.
  const [impPhase, setImpPhase] = useState("idle");
  const [impFileName, setImpFileName] = useState("");
  // Picked file waits here until the user clicks Import — nothing uploads on drop.
  const [impFile, setImpFile] = useState(null);
  // PDFs stage as a batch (up to MAX_IMPORT_PDFS); Excel/CSV stays one file at a time.
  const [impFiles, setImpFiles] = useState([]);
  const [impProgress, setImpProgress] = useState(null); // { uploaded, done, total }
  const [impJobId, setImpJobId] = useState("");
  const [impColumns, setImpColumns] = useState([]);
  const [impSample, setImpSample] = useState([]);
  const [impFields, setImpFields] = useState([]);
  const [impMapping, setImpMapping] = useState({});
  const [impTotalRows, setImpTotalRows] = useState(0);
  const [impError, setImpError] = useState("");
  const [impSubmitting, setImpSubmitting] = useState(false);
  // Scout-by-company: paste a careers URL → name → track.
  const [scoutUrl, setScoutUrl] = useState("");
  const [scoutPhase, setScoutPhase] = useState("idle"); // idle|tracking|invalid
  const [scoutCompany, setScoutCompany] = useState("");
  // Domain of the Brandfetch pick, so the server fetches the right logo. Cleared on typing.
  const [scoutDomain, setScoutDomain] = useState("");
  const [scoutError, setScoutError] = useState("");
  // Sources to pull from: the careers page plus job boards.
  const [scoutCareers, setScoutCareers] = useState(false);
  const [scoutPlatforms, setScoutPlatforms] = useState(() => new Set());
  const { features: planFeatures } = usePlan();
  const boardsLocked = !planFeatures.thirdPartyBoards;
  // Per ticked board: { loading, failed, list: [{platformId,name,url?}], pick: platformId | "" (nothing pinned yet) }.
  const [candState, setCandState] = useState({});
  const candSeq = useRef(0);
  // JobsDB markets of the picked candidate: null | { loading: true } | { markets: [{name,count,url}] }.
  const [jobsdbMarkets, setJobsdbMarkets] = useState(null);
  const jobsdbSeq = useRef({ key: "", n: 0 });
  // Scout-by-industry: pick an industry → tick firms from the public corpus.
  const [indPhase, setIndPhase] = useState("list"); // list|firms|saving|error
  const [indList, setIndList] = useState(null); // null = loading
  const [indKey, setIndKey] = useState("");
  const [indLabel, setIndLabel] = useState("");
  const [indFirms, setIndFirms] = useState([]);
  const [indPicked, setIndPicked] = useState(() => new Set());
  // Firms already picked when the list opened — only NEW ticks are charged.
  const [indInitial, setIndInitial] = useState(() => new Set());
  const [indFirmCredits, setIndFirmCredits] = useState(0);
  const [indSearch, setIndSearch] = useState("");
  const [indError, setIndError] = useState("");
  const fileInputRef = useRef(null);
  // The PDF run this modal is showing; closing detaches it so it can't touch a reopened modal.
  const pdfRunRef = useRef(0);

  useEffect(() => {
    if (!open) {
      pdfRunRef.current += 1;
      setTab(initialTab);
      setImpPhase("idle");
      setImpFileName("");
      setImpFile(null);
      setImpFiles([]);
      setImpProgress(null);
      setImpJobId("");
      setImpColumns([]);
      setImpSample([]);
      setImpFields([]);
      setImpMapping({});
      setImpTotalRows(0);
      setImpError("");
      setImpSubmitting(false);
      setScoutUrl("");
      setScoutPhase("idle");
      setScoutCompany("");
      setScoutError("");
      setScoutCareers(false);
      setScoutPlatforms(new Set());
      setCandState({});
      setJobsdbMarkets(null);
      jobsdbSeq.current = { key: "", n: 0 };
      setIndPhase("list");
      setIndList(null);
      setIndKey("");
      setIndLabel("");
      setIndFirms([]);
      setIndPicked(new Set());
      setIndSearch("");
      setIndError("");
    }
  }, [open, initialTab]);
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab, open]);
  useLockPageScroll(open);

  // A lookup is for one exact name: editing the name throws its matches away, so Track waits for a fresh find.
  useEffect(() => {
    if (!open) return;
    candSeq.current += 1;
    setCandState({});
  }, [open, scoutCompany]);

  // The candidate list's "None of these" value; distinct from "" so the list can open on "Choose a company…".
  const NONE_OF_THESE = "__none__";

  // One board's live lookup (directory + the board's own search); a board that didn't answer is `failed` and offers a retry.
  async function lookupBoard(k, seq) {
    const q = scoutCompany.trim();
    setCandState((prev) => ({ ...prev, [k]: { loading: true, failed: false, list: [], pick: "" } }));
    let list = [];
    let failed = false;
    try {
      const res = await fetchWithAuth(`/api/scout/platforms/${k}/companies?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      list = Array.isArray(data) ? data : [];
    } catch (_) {
      failed = true;
    }
    if (seq !== candSeq.current) return;
    setCandState((prev) => ({
      ...prev,
      // Only a confident match is pre-selected; a loose one is offered but nothing is pinned until the user picks it.
      [k]: {
        loading: false,
        failed,
        list,
        pick: list[0]?.score >= 0.8 ? String(list[0].platformId) : "",
      },
    }));
  }

  // "Find on job boards": every ticked board at once; replies to an older Find are dropped.
  function findBoards() {
    if (!scoutCompany.trim() || !scoutPlatforms.size) return;
    const seq = ++candSeq.current;
    for (const k of scoutPlatforms) lookupBoard(k, seq);
  }

  // JobsDB markets of the picked candidate, refetched only when the pick changes; never gates Track.
  useEffect(() => {
    if (!open) return;
    const c = candState.jobsdb;
    const hit =
      c && !c.loading && c.pick ? c.list.find((x) => String(x.platformId) === c.pick) : null;
    const key = hit ? String(hit.platformId) : "";
    if (jobsdbSeq.current.key === key) return;
    const seq = { key, n: jobsdbSeq.current.n + 1 };
    jobsdbSeq.current = seq;
    if (!hit) {
      setJobsdbMarkets(null);
      return;
    }
    setJobsdbMarkets({ loading: true });
    (async () => {
      let markets = [];
      try {
        const res = await fetchWithAuth(
          `/api/scout/platforms/jobsdb/companies/${encodeURIComponent(key)}/markets`,
        );
        if (!res.ok) throw new Error(String(res.status));
        const d = await res.json();
        if (!d.failed && Array.isArray(d.markets)) markets = d.markets.filter((m) => m.url);
      } catch (_) {
        markets = [];
      }
      if (jobsdbSeq.current.n !== seq.n) return;
      setJobsdbMarkets({ markets });
    })();
  }, [open, candState]);

  // Load the industry list when the tab is shown; jump straight into a firm
  // list when opened from "Edit selection" on the Tracked tab.
  useEffect(() => {
    if (!open || tab !== "industry") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth("/api/industries");
        const data = res.ok ? await res.json() : [];
        if (cancelled) return;
        setIndList(Array.isArray(data) ? data : []);
        if (initialIndustry) openIndustry(initialIndustry);
      } catch (_) {
        if (!cancelled) setIndList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  if (!open) return null;

  async function openIndustry(key) {
    setIndKey(key);
    setIndFirms([]);
    setIndSearch("");
    setIndError("");
    setIndPhase("firms");
    try {
      const res = await fetchWithAuth(`/api/industries/${encodeURIComponent(key)}/firms`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Couldn't load firms.");
      setIndLabel(data.label || key);
      setIndFirms(data.firms || []);
      const selected = new Set((data.firms || []).filter((f) => f.selected).map((f) => f.name));
      setIndPicked(selected);
      setIndInitial(new Set(selected));
      setIndFirmCredits(Number(data.firmCredits) || 0);
    } catch (e) {
      setIndError(e.message || "Couldn't load firms.");
      setIndPhase("error");
    }
  }
  function toggleFirm(name) {
    setIndPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }
  const indVisible = indFirms.filter((f) =>
    f.name.toLowerCase().includes(indSearch.trim().toLowerCase()),
  );
  const indPickedJobs = indFirms.reduce(
    (sum, f) => (indPicked.has(f.name) ? sum + (f.jobCount || 0) : sum),
    0,
  );
  const indAddedNames = [...indPicked].filter((n) => !indInitial.has(n));
  const indRemovedNames = [...indInitial].filter((n) => !indPicked.has(n));
  const indAdded = indAddedNames.length;
  const indRemoved = indRemovedNames.length;
  const indCost = indAdded * indFirmCredits;
  // Any paid add or any removal gets a confirmation step first; an unchanged
  // list just saves (nothing to warn about).
  function requestSaveIndustry() {
    if (indAdded === 0 && indRemoved === 0) return saveIndustry();
    setIndError("");
    setIndPhase("confirm");
  }
  function pickVisible(on) {
    setIndPicked((prev) => {
      const next = new Set(prev);
      indVisible.forEach((f) => (on ? next.add(f.name) : next.delete(f.name)));
      return next;
    });
  }
  async function saveIndustry() {
    setIndPhase("saving");
    try {
      const res = await fetchWithAuth(
        `/api/industries/${encodeURIComponent(indKey)}/selection`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sources: [...indPicked] }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Couldn't save your selection.");
      onIndustrySaved && onIndustrySaved(data);
      if (data?.charged > 0) creditsChanged();
      onClose();
    } catch (e) {
      setIndError(e.message || "Couldn't save your selection.");
      setIndPhase("error");
    }
  }

  // Upload the file. PDFs queue immediately; Excel/CSV comes back with columns
  // + suggested mappings for the user to confirm before it queues.
  async function uploadImportFile(file) {
    if (!file) return;
    setImpFileName(file.name);
    setImpError("");
    setImpPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetchWithAuth("/api/jobs/import", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Couldn't upload this file.");
      if (data.fileType === "pdf") {
        onQueued && onQueued(data.jobId, file.name);
        onClose();
        return;
      }
      setImpJobId(data.jobId);
      setImpColumns(data.columns || []);
      setImpSample(data.sample || []);
      setImpFields(data.fields || []);
      setImpMapping(data.suggestedMapping || {});
      setImpTotalRows(data.totalRows || 0);
      setImpPhase("mapping");
    } catch (e) {
      setImpError(e.message || "Couldn't upload this file.");
      setImpPhase("invalid");
    }
  }

  const MAX_IMPORT_PDFS = 50;
  const MAX_IMPORT_BYTES = 8 * 1024 * 1024; // mirrors the server's per-file cap

  function stageImportFiles(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setImpError("");
    const tooBig = files.find((f) => f.size > MAX_IMPORT_BYTES);
    if (tooBig) {
      setImpError(`${tooBig.name} is too large (max 8MB per file).`);
      return;
    }
    const pdfs = files.filter((f) => /\.pdf$/i.test(f.name));
    const sheets = files.filter((f) => !/\.pdf$/i.test(f.name));
    if (sheets.length) {
      // A spreadsheet goes through column mapping, so it travels alone.
      setImpFiles([]);
      setImpFile(sheets[0]);
      setImpFileName(sheets[0].name);
      return;
    }
    setImpFile(null);
    setImpFileName("");
    setImpFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      const merged = [...prev, ...pdfs.filter((f) => !seen.has(`${f.name}:${f.size}`))];
      if (merged.length > MAX_IMPORT_PDFS) {
        setImpError(`Up to ${MAX_IMPORT_PDFS} PDFs at once — the first ${MAX_IMPORT_PDFS} are kept.`);
      }
      return merged.slice(0, MAX_IMPORT_PDFS);
    });
  }

  function onImportFilePicked(e) {
    const files = e.target.files;
    stageImportFiles(files);
    e.target.value = "";
  }

  const impIsPdf = /\.pdf$/i.test(impFile?.name || "");

  // Upload the staged PDFs one request at a time (the import endpoint takes one file),
  // then watch every queued job until the worker has read them all. The bar counts
  // finished files, so it climbs 1/N … N/N.
  async function importPdfBatch() {
    const files = impFiles;
    if (!files.length) return;
    setImpError("");
    setImpPhase("progress");
    setImpProgress({ uploaded: 0, done: 0, total: files.length });
    const run = ++pdfRunRef.current;
    const live = () => pdfRunRef.current === run;
    const jobs = [];
    let failed = 0;
    const failedNames = [];
    // One id for the whole staged set, so Background jobs shows the upload as one entry.
    const batchId = newBatchId();
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("batchId", batchId);
        fd.append("batchTotal", String(files.length));
        const res = await fetchWithAuth("/api/jobs/import", { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.jobId) throw new Error(data.error || "upload failed");
        jobs.push({ jobId: data.jobId, name: file.name, status: "queued", imported: 0 });
      } catch (err) {
        failed += 1;
        failedNames.push(`${file.name} (${err?.message || "upload failed"})`);
      }
      if (live()) setImpProgress((p) => p && { ...p, uploaded: p.uploaded + 1, done: failed });
    }
    let imported = 0;
    const deadline = Date.now() + 10 * 60 * 1000 + files.length * 60 * 1000;
    while (jobs.some((j) => j.status === "queued") && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      await Promise.all(
        jobs
          .filter((j) => j.status === "queued")
          .map(async (j) => {
            try {
              const res = await fetchWithAuth(`/api/jobs/import/${j.jobId}/status`);
              if (!res.ok) return;
              const data = await res.json();
              if (data.status === "succeeded") {
                j.status = "done";
                j.imported = data.result?.imported ?? 0;
                imported += j.imported;
              } else if (data.status === "failed") {
                j.status = "failed";
                failed += 1;
                failedNames.push(`${j.name} (${data.error || "couldn't be read"})`);
              }
            } catch (_) {
              /* transient poll error — keep trying */
            }
          }),
      );
      if (live()) setImpProgress((p) => p && { ...p, done: jobs.filter((j) => j.status !== "queued").length + (files.length - jobs.length) });
    }
    const unfinished = jobs.filter((j) => j.status === "queued").length;
    onImportFinished &&
      onImportFinished({ files: files.length, imported, failed, unfinished, failedNames });
    if (live()) onClose();
  }

  async function confirmImportMapping() {
    if (!impMapping.title || !impMapping.description || impSubmitting) return;
    setImpSubmitting(true);
    setImpError("");
    try {
      const res = await fetchWithAuth(`/api/jobs/import/${impJobId}/mapping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mapping: impMapping }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Couldn't start the import.");
      onQueued && onQueued(impJobId, impFileName);
      onClose();
    } catch (e) {
      setImpError(e.message || "Couldn't start the import.");
      setImpSubmitting(false);
    }
  }

  async function downloadTemplate() {
    try {
      const res = await fetchWithAuth("/api/jobs/import/template");
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jobs-import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* download is best-effort */
    }
  }

  // `platforms` for the track call: every ticked board, Worldwide, with the company the user pinned on it
  // (Track is disabled until every board has one, and the server refuses a board without an id).
  function platformsPayload() {
    const out = {};
    for (const k of scoutPlatforms) {
      const c = candState[k];
      const hit = c?.pick ? c.list.find((x) => String(x.platformId) === c.pick) : null;
      if (hit) out[k] = { country: "Worldwide", platformId: String(hit.platformId), matchedName: hit.name };
    }
    return Object.keys(out).length ? out : undefined;
  }
  // Every ticked board has finished its lookup (its pick is then a candidate or "").
  const candsLooked = [...scoutPlatforms].every((k) => candState[k] && !candState[k].loading);
  const foundBoards = candsLooked ? [...scoutPlatforms].filter((k) => candState[k].pick) : [];
  // No pick = skipped at Track: not on the board, only loose look-alikes, or "None of these".
  const missingBoards = candsLooked ? [...scoutPlatforms].filter((k) => !candState[k].pick && !candState[k].failed) : [];
  const unreached = candsLooked ? [...scoutPlatforms].filter((k) => candState[k].failed) : [];
  // Ready once every board answered, and at least one source is left to pull from.
  const candsReady = candsLooked && unreached.length === 0 && (scoutCareers || foundBoards.length > 0);
  const anyLooking = [...scoutPlatforms].some((k) => candState[k]?.loading);
  const canFind = !!scoutCompany.trim() && scoutPlatforms.size > 0 && !anyLooking;
  // The link cell beside a board's select: the candidate's own page, or JobsDB's per-market pages.
  function renderViewJobs(k, picked) {
    if (k !== "jobsdb") {
      return picked?.url ? (
        <a className="view" href={picked.url} target="_blank" rel="noreferrer">
          View jobs <FiExternalLink size={12} />
        </a>
      ) : (
        <span />
      );
    }
    const m = picked ? jobsdbMarkets : null;
    if (!m) return <span />;
    if (m.loading) return <span className="view off">View jobs</span>;
    if (m.markets.length === 1) {
      return (
        <a className="view" href={m.markets[0].url} target="_blank" rel="noreferrer">
          View jobs <FiExternalLink size={12} />
        </a>
      );
    }
    return m.markets.length > 1 ? <JobsdbMarketsMenu markets={m.markets} /> : <span />;
  }
  // Unticking a board drops its lookup, so ticking it again starts with a fresh Find.
  function togglePlatformPick(k) {
    const on = scoutPlatforms.has(k);
    setScoutPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
    if (on) {
      setCandState((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    }
  }
  // At least one source, and a URL whenever the careers page is one of them.
  const anySource = scoutCareers || scoutPlatforms.size > 0;
  // Source tiles, the careers URL, and one candidate picker per ticked board.
  function renderSourcePicker() {
    return (
      <PlatformPick data-tour="jt-sources">
        <FieldLabel>Pull postings from</FieldLabel>
        <div className="boxes">
          <label className="box" data-on={scoutCareers} data-tour="jt-careers">
            <input
              type="checkbox"
              checked={scoutCareers}
              onChange={() => setScoutCareers((v) => !v)}
            />
            <FiGlobe size={18} className="globe" /> Official Careers Page
          </label>
          {Object.entries(PLATFORMS).map(([k, p]) => (
            <label key={k} className="box" data-on={scoutPlatforms.has(k)} data-tour={`jt-board-${k}`} style={boardsLocked ? { opacity: 0.6 } : undefined}>
              <input
                type="checkbox"
                checked={scoutPlatforms.has(k)}
                disabled={boardsLocked}
                onChange={() => togglePlatformPick(k)}
              />
              <PlatformLogo platform={k} size={18} /> {p.label}
            </label>
          ))}
        </div>
        {boardsLocked && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 13, color: "var(--ink-muted)" }} data-tour="jt-boards-locked">
            <PlanLock plan="Growth" label="Third-party job boards" />
            Pulling from LinkedIn and other job boards is part of the Growth plan. The careers page is included on every plan.
          </div>
        )}
        {scoutCareers && (
          <div className="url" data-tour="jt-url">
            <FieldLabel>Careers Page URL</FieldLabel>
            <ScoutInput>
              <FiGlobe size={14} className="lead" />
              <input
                placeholder="https://apply.workable.com/stone-king/"
                value={scoutUrl}
                onChange={(e) => setScoutUrl(e.target.value)}
              />
            </ScoutInput>
          </div>
        )}
        {scoutPlatforms.size > 0 && (
          <div className="cands" data-tour="jt-cands">
            {candsReady && <span hidden data-tour="jt-cands-ready" />}
            <div className="head">
              <span>
                {anyLooking
                  ? "Searching each board…"
                  : candsReady
                    ? missingBoards.length
                      ? "Boards without this company are skipped. Check the matches below."
                      : "The best match on each board is picked for you — change it if it's wrong."
                    : unreached.length
                      ? `Retry ${unreached.map((k) => PLATFORMS[k].label).join(" and ")}, or untick it to continue.`
                      : candsLooked && !foundBoards.length && !scoutCareers
                        ? `${scoutCompany.trim() || "This company"} isn't on any board you ticked. Try another spelling, or add the careers page.`
                        : `Find ${scoutCompany.trim() || "the company"} on the boards you ticked.`}
              </span>
              <Btn type="button" onClick={findBoards} disabled={!canFind} data-tour="jt-find-boards">
                {anyLooking ? <FiRefreshCw size={13} className="spin" /> : <FiSearch size={13} />}{" "}
                {candsReady ? "Find again" : "Find on job boards"}
              </Btn>
            </div>
            {Object.keys(PLATFORMS)
              .filter((k) => scoutPlatforms.has(k) && candState[k])
              .map((k) => {
                const c = candState[k];
                const picked = c && !c.loading ? c.list.find((x) => String(x.platformId) === c.pick) : null;
                const loose = c && !c.loading && !c.failed && c.list.length > 0 && !c.pick && !c.browse;
                if (c && !c.loading && (c.failed || !c.list.length || loose)) {
                  return (
                    <div className="cand" key={k}>
                      <span className="lbl">
                        <PlatformLogo platform={k} size={16} /> {PLATFORMS[k].label}
                      </span>
                      <span className={`miss${c.failed ? " warn" : ""}`}>
                        {c.failed ? <FiAlertCircle size={13} /> : <FiMinusCircle size={13} />}
                        {c.failed
                          ? `Couldn't reach ${PLATFORMS[k].label}`
                          : loose
                            ? `No exact match on ${PLATFORMS[k].label}`
                            : `Not on ${PLATFORMS[k].label}`}
                      </span>
                      {c.failed ? (
                        <button type="button" className="view" onClick={() => lookupBoard(k, candSeq.current)}>
                          <FiRefreshCw size={12} /> Retry
                        </button>
                      ) : loose ? (
                        <button
                          type="button"
                          className="view"
                          onClick={() => setCandState((prev) => ({ ...prev, [k]: { ...prev[k], browse: true } }))}
                        >
                          See {c.list.length} similar
                        </button>
                      ) : (
                        <span />
                      )}
                    </div>
                  );
                }
                return (
                  <div className="cand" key={k}>
                    <span className="lbl">
                      <PlatformLogo platform={k} size={16} /> {PLATFORMS[k].label}
                    </span>
                    <select
                      value={c && !c.loading ? c.pick : "…"}
                      disabled={!c || c.loading}
                      autoFocus={!!c?.browse && !c.pick}
                      // "None of these", or leaving the list without a pick, folds back to "No exact match".
                      onChange={(e) =>
                        setCandState((prev) => {
                          const v = e.target.value === NONE_OF_THESE ? "" : e.target.value;
                          return { ...prev, [k]: { ...prev[k], pick: v, browse: v ? prev[k].browse : false } };
                        })
                      }
                      onBlur={() =>
                        setCandState((prev) =>
                          prev[k] && !prev[k].pick ? { ...prev, [k]: { ...prev[k], browse: false } } : prev,
                        )
                      }
                    >
                      {(!c || c.loading) && <option value="…">Searching…</option>}
                      {c && !c.loading && !c.pick && (
                        <option value="" disabled>
                          Choose a company…
                        </option>
                      )}
                      {c &&
                        !c.loading &&
                        c.list.map((x) => (
                          <option key={String(x.platformId)} value={String(x.platformId)}>
                            {x.name}
                          </option>
                        ))}
                      {c && !c.loading && <option value={NONE_OF_THESE}>None of these — skip {PLATFORMS[k].label}</option>}
                    </select>
                    {renderViewJobs(k, picked)}
                  </div>
                );
              })}
          </div>
        )}
        {scoutPlatforms.size > 0 && (
          <div className="scope">
            <FiGlobe size={12} />
            {[...scoutPlatforms].map((k) => PLATFORMS[k].label).join(", ")} — searched{" "}
            <b>worldwide</b>, every country the company posts in.
          </div>
        )}
      </PlatformPick>
    );
  }

  // Track (subscribe to) the company via /api/scout/track.
  async function trackCompany() {
    const url = scoutCareers ? scoutUrl.trim() : "";
    const companyName = scoutCompany.trim();
    if (!companyName || !anySource || (scoutCareers && !url) || !candsReady) return;
    setScoutPhase("tracking");
    try {
      const res = await fetchWithAuth("/api/scout/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: url || undefined,
          companyName,
          domain: scoutDomain || undefined,
          platforms: platformsPayload(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Couldn't track this company.");
      onTrack && onTrack(companyName, data);
      onClose();
    } catch (e) {
      setScoutError(e.message || "Failed to track.");
      setScoutPhase("invalid");
    }
  }

  const mappingReady = !!(impMapping.title && impMapping.description);

  return createPortal(
    <ModalBackdrop>
      <Modal>
        <ModalHead>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <ModalH>Add jobs</ModalH>
              <ModalSub>
                {importEnabled
                  ? "Import job descriptions or find jobs from a company's careers page."
                  : "Track a company and we'll pull its open roles and re-check daily."}
              </ModalSub>
            </div>
            <IconBtn onClick={onClose} data-tour="jt-close">
              <FiX size={16} />
            </IconBtn>
          </div>
          {/* One source only (import and industry parked): no tab strip to pick from. */}
          {(importEnabled || industryEnabled) && (
            <FieldTabs>
              {importEnabled && (
                <Tab $active={tab === "import"} onClick={() => setTab("import")}>
                  <FiUpload size={12} /> Import jobs
                </Tab>
              )}
              <Tab $active={tab === "scout"} onClick={() => setTab("scout")}>
                <Spark size={12} /> Find jobs
              </Tab>
              {industryEnabled && (
                <Tab $active={tab === "industry"} onClick={() => setTab("industry")}>
                  <FiGrid size={12} /> Scout by industry
                </Tab>
              )}
            </FieldTabs>
          )}
        </ModalHead>

        <ModalBody>
          {tab === "import" && impPhase === "idle" && (
            <>
              <Dropzone
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  stageImportFiles(e.dataTransfer?.files);
                }}
              >
                <div className="icon">
                  {impFile || impFiles.length ? <FiFileText size={20} /> : <FiUpload size={20} />}
                </div>
                {impFiles.length > 0 ? (
                  <>
                    <div className="h">
                      {impFiles.length} PDF{impFiles.length === 1 ? "" : "s"} ready
                    </div>
                    <div className="sub">
                      Nothing is read until you click Import. Drop more to add them
                      (up to {MAX_IMPORT_PDFS}).
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <Btn as="span">Add more</Btn>
                      <Btn
                        $ghost
                        as="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImpFiles([]);
                        }}
                      >
                        Clear all
                      </Btn>
                    </div>
                  </>
                ) : impFile ? (
                  <>
                    <div className="h">{impFile.name}</div>
                    <div className="sub">
                      {(impFile.size / 1024).toFixed(0)} KB ·{" "}
                      {impIsPdf
                        ? "AI will read each posting when you click Import."
                        : "You'll match its columns to our fields next."}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <Btn as="span">Choose another file</Btn>
                      <Btn
                        $ghost
                        as="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImpFile(null);
                          setImpFileName("");
                        }}
                      >
                        Remove
                      </Btn>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h">Drop PDFs or an Excel file</div>
                    <div className="sub">
                      PDFs are read with AI, one posting at a time — up to {MAX_IMPORT_PDFS} at
                      once; Excel columns are matched to our fields with your
                      confirmation. Up to 500 roles. These jobs stay private to your team.
                    </div>
                    <Btn style={{ marginTop: 4 }} as="span">
                      Browse files
                    </Btn>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.csv"
                  multiple
                  hidden
                  onChange={onImportFilePicked}
                />
              </Dropzone>
              {impFiles.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    maxHeight: 220,
                    overflowY: "auto",
                    border: "1px solid var(--line)",
                    borderRadius: 10,
                  }}
                >
                  {impFiles.map((f, i) => (
                    <div
                      key={`${f.name}:${f.size}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "7px 12px",
                        fontSize: 13,
                        borderTop: i ? "1px solid var(--line)" : "none",
                      }}
                    >
                      <span style={{ color: "var(--ink-muted)", width: 22, textAlign: "right" }}>
                        {i + 1}.
                      </span>
                      <FiFileText size={13} style={{ flexShrink: 0 }} />
                      <span
                        title={f.name}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {f.name}
                      </span>
                      <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                      <Btn
                        $ghost
                        $sm
                        aria-label={`Remove ${f.name}`}
                        onClick={() => setImpFiles((prev) => prev.filter((x) => x !== f))}
                      >
                        <FiX size={12} />
                      </Btn>
                    </div>
                  ))}
                </div>
              )}
              {impError && (
                <div style={{ color: "var(--danger)", fontSize: 12.5, marginTop: 8 }}>
                  {impError}
                </div>
              )}
              <TemplateRow>
                <FiFileText size={12} />
                Using Excel? Start from our template so columns map cleanly.
                <Btn
                  $ghost
                  $sm
                  onClick={downloadTemplate}
                  style={{ marginLeft: "auto", flexShrink: 0 }}
                >
                  Download template
                </Btn>
              </TemplateRow>
            </>
          )}

          {tab === "import" && impPhase === "uploading" && (
            <PhaseLoading>
              <FiRefreshCw size={24} className="spin" />
              <div className="h">Reading {impFileName}…</div>
              <div className="sub">Checking the file and preparing the import.</div>
            </PhaseLoading>
          )}

          {tab === "import" && impPhase === "progress" && impProgress && (
            <PhaseLoading>
              <FiRefreshCw size={24} className="spin" />
              <div className="h">
                Importing {impProgress.done}/{impProgress.total}
              </div>
              <div className="sub">
                {impProgress.uploaded < impProgress.total
                  ? `Uploading ${impProgress.uploaded}/${impProgress.total} — reading starts as each one lands.`
                  : "All uploaded — the AI is reading each posting."}
              </div>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={impProgress.total}
                aria-valuenow={impProgress.done}
                style={{
                  width: "100%",
                  maxWidth: 360,
                  height: 6,
                  marginTop: 14,
                  borderRadius: 999,
                  background: "var(--line)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(impProgress.done / impProgress.total) * 100}%`,
                    background: "var(--accent, currentColor)",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </PhaseLoading>
          )}

          {tab === "import" && impPhase === "invalid" && (
            <ReviewBanner>
              <span className="firm-badge" style={{ background: "var(--danger)" }}>
                !
              </span>
              <div>
                <b>Couldn't read this file</b>
                <div className="sub">{impError || "Try another file."}</div>
              </div>
              <Btn
                $sm
                style={{ marginLeft: "auto" }}
                onClick={() => {
                  setImpError("");
                  setImpPhase("idle");
                }}
              >
                <FiRefreshCw size={12} /> Try another
              </Btn>
            </ReviewBanner>
          )}

          {tab === "import" && impPhase === "mapping" && (
            <>
              <ReviewBanner>
                <span className="review-badge">
                  <FiCheck size={12} strokeWidth={3} />
                </span>
                <div>
                  <b>{impTotalRows} rows found</b> in {impFileName}
                  <div className="sub">
                    Match your file's columns to our fields — title and
                    description are required.
                  </div>
                </div>
              </ReviewBanner>
              {impError && (
                <div
                  style={{
                    color: "var(--danger)",
                    fontSize: 12.5,
                    marginBottom: 10,
                  }}
                >
                  {impError}
                </div>
              )}
              <MapList>
                {impFields.map((f) => {
                  const col = impMapping[f.key] || "";
                  const sampleRow = col
                    ? impSample.find((r) => String(r[col] || "").trim())
                    : null;
                  const sampleVal = sampleRow ? String(sampleRow[col]) : "";
                  return (
                    <MapRow key={f.key}>
                      <div className="field">
                        {f.label}
                        {f.required && <span className="req">*</span>}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <select
                          value={col}
                          onChange={(e) => {
                            const v = e.target.value;
                            setImpMapping((m) => {
                              const next = { ...m };
                              if (v) next[f.key] = v;
                              else delete next[f.key];
                              return next;
                            });
                          }}
                        >
                          <option value="">— don't import —</option>
                          {impColumns.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {col && sampleVal && (
                          <div className="sample" title={sampleVal}>
                            e.g. {sampleVal}
                          </div>
                        )}
                      </div>
                    </MapRow>
                  );
                })}
              </MapList>
            </>
          )}

          {tab === "scout" && scoutPhase === "idle" && (
            <>
              <ScoutHint style={{ marginTop: 0, marginBottom: 14 }}>
                <Spark
                  size={14}
                  style={{ color: "var(--accent)", marginTop: 1, flexShrink: 0 }}
                />
                <div>
                  Enter the company's <b>name</b> and tick where to pull its postings
                  from — its <b>careers page</b> (we detect the system) and/or the job
                  boards. We import the open roles and <b>re-check daily</b>. These
                  jobs are private to you.
                </div>
              </ScoutHint>
              <div style={{ marginBottom: 12 }} data-tour="jt-name">
                <FieldLabel>Company name</FieldLabel>
                <CompanyTypeahead
                  value={scoutCompany}
                  onChange={(v, domain) => {
                    setScoutCompany(v);
                    setScoutDomain(domain || "");
                  }}
                  onEnter={() => (candsReady || scoutPlatforms.size === 0 ? trackCompany() : findBoards())}
                />
              </div>
              {renderSourcePicker()}
            </>
          )}

          {tab === "scout" && scoutPhase === "tracking" && (
            <PhaseLoading>
              <FiRefreshCw size={24} className="spin" />
              <div className="h">Tracking {scoutCompany}…</div>
              <div className="sub">
                Importing roles in the background — they'll appear in your Jobs
                list shortly.
              </div>
            </PhaseLoading>
          )}

          {tab === "scout" && scoutPhase === "invalid" && (
            <ReviewBanner>
              <span className="firm-badge" style={{ background: "var(--danger)" }}>
                !
              </span>
              <div>
                <b>Couldn't scout this URL</b>
                <div className="sub">
                  {scoutError || "Try another link."}
                </div>
              </div>
              <Btn
                $sm
                style={{ marginLeft: "auto" }}
                onClick={() => setScoutPhase("idle")}
              >
                <FiRefreshCw size={12} /> Try another
              </Btn>
            </ReviewBanner>
          )}

          {tab === "industry" && indPhase === "list" && (
            <>
              <FieldLabel>Choose an industry</FieldLabel>
              {indList === null ? (
                <PhaseLoading>
                  <FiRefreshCw size={24} className="spin" />
                  <div className="h">Loading industries…</div>
                </PhaseLoading>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {indList.map((ind) => (
                    <IndustryCard key={ind.key} type="button" onClick={() => openIndustry(ind.key)}>
                      <span className="icon">
                        <FiBriefcase size={18} />
                      </span>
                      <span>
                        <div className="h">{ind.label}</div>
                        <div className="sub">
                          {ind.firmCount.toLocaleString()} firms ·{" "}
                          {ind.jobCount.toLocaleString()} open roles
                        </div>
                      </span>
                      <span className="picked">
                        {ind.selectedCount > 0
                          ? `${ind.selectedCount} picked`
                          : "Pick firms →"}
                      </span>
                    </IndustryCard>
                  ))}
                </div>
              )}
              <ScoutHint>
                <Spark size={14} style={{ color: "var(--accent)", marginTop: 1, flexShrink: 0 }} />
                <div>
                  Select an industry — we've picked the <b>top companies</b> in
                  each one. Choose the ones you want and their roles are added to{" "}
                  <b>Tracked jobs</b>.
                </div>
              </ScoutHint>
            </>
          )}

          {tab === "industry" && indPhase === "firms" && (
            <>
              <FirmToolbar>
                <LinkBtn type="button" onClick={() => setIndPhase("list")}>
                  ← Industries
                </LinkBtn>
                <span className="spacer" />
                <LinkBtn type="button" onClick={() => pickVisible(true)}>
                  Select {indSearch ? "shown" : "all"}
                </LinkBtn>
                <LinkBtn type="button" onClick={() => pickVisible(false)}>
                  Clear {indSearch ? "shown" : "all"}
                </LinkBtn>
              </FirmToolbar>
              <div style={{ marginBottom: 10 }}>
                <ScoutInput>
                  <FiSearch size={14} className="lead" />
                  <input
                    autoFocus
                    placeholder={`Search ${indLabel || "industry"} firms…`}
                    value={indSearch}
                    onChange={(e) => setIndSearch(e.target.value)}
                  />
                </ScoutInput>
              </div>
              {indFirms.length === 0 ? (
                <PhaseLoading>
                  <FiRefreshCw size={24} className="spin" />
                  <div className="h">Loading firms…</div>
                </PhaseLoading>
              ) : indVisible.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--ink-muted)", fontSize: 13 }}>
                  No firms match "{indSearch}".
                </div>
              ) : (
                <FirmList>
                  {indVisible.map((f) => (
                    <FirmRow key={f.name}>
                      <input
                        type="checkbox"
                        checked={indPicked.has(f.name)}
                        onChange={() => toggleFirm(f.name)}
                      />
                      <span className="name">{f.name}</span>
                      <span className="n">
                        {f.jobCount} {f.jobCount === 1 ? "role" : "roles"}
                      </span>
                    </FirmRow>
                  ))}
                </FirmList>
              )}
            </>
          )}

          {tab === "industry" && indPhase === "confirm" && (
            <ConfirmCard>
              <div className="head">
                <div className="title">Confirm your changes</div>
                <div className="total">
                  {indCost > 0 ? (
                    <>
                      <b>{indCost.toLocaleString()}</b>credits charged now
                    </>
                  ) : (
                    "No charge"
                  )}
                </div>
              </div>
              {indAdded > 0 && (
                <div className="group add">
                  <div className="label">
                    <span>
                      Adding {indAdded} {indAdded === 1 ? "firm" : "firms"}
                    </span>
                    <span className="meta">
                      {indFirmCredits.toLocaleString()} credits each
                    </span>
                  </div>
                  <div className="chips">
                    {indAddedNames.slice(0, 12).map((n) => (
                      <span className="chip" key={n}>
                        {n}
                      </span>
                    ))}
                    {indAdded > 12 && (
                      <span className="chip more">+{indAdded - 12} more</span>
                    )}
                  </div>
                </div>
              )}
              {indRemoved > 0 && (
                <div className="group remove">
                  <div className="label">
                    <span>
                      Removing {indRemoved} {indRemoved === 1 ? "firm" : "firms"}
                    </span>
                    <span className="meta">not refunded</span>
                  </div>
                  <div className="chips">
                    {indRemovedNames.slice(0, 12).map((n) => (
                      <span className="chip" key={n}>
                        {n}
                      </span>
                    ))}
                    {indRemoved > 12 && (
                      <span className="chip more">+{indRemoved - 12} more</span>
                    )}
                  </div>
                </div>
              )}
              <ul className="fine">
                <li>
                  Each firm you add is charged once (<b>{indFirmCredits.toLocaleString()} credits</b>).
                  Keeping it afterwards is free.
                </li>
                <li>Removing a firm takes its roles out of Tracked jobs. The charge isn't refunded.</li>
                <li>
                  Adding a removed firm back costs <b>{indFirmCredits.toLocaleString()} credits</b> again.
                </li>
              </ul>
            </ConfirmCard>
          )}

          {tab === "industry" && indPhase === "saving" && (
            <PhaseLoading>
              <FiRefreshCw size={24} className="spin" />
              <div className="h">Saving your {indLabel} firms…</div>
              <div className="sub">Their open roles will show in Tracked jobs.</div>
            </PhaseLoading>
          )}

          {tab === "industry" && indPhase === "error" && (
            <ReviewBanner>
              <span className="firm-badge" style={{ background: "var(--danger)" }}>
                !
              </span>
              <div>
                <b>Something went wrong</b>
                <div className="sub">{indError || "Please try again."}</div>
              </div>
              <Btn $sm style={{ marginLeft: "auto" }} onClick={() => setIndPhase("list")}>
                <FiRefreshCw size={12} /> Try again
              </Btn>
            </ReviewBanner>
          )}
        </ModalBody>
        <ModalFoot>
          {tab === "industry" && indPhase === "firms" && (
            <FootCount>
              <b>{indPicked.size}</b> {indPicked.size === 1 ? "firm" : "firms"} ·{" "}
              <b>{indPickedJobs.toLocaleString()}</b> roles
            </FootCount>
          )}
          {tab === "import" && impPhase === "mapping" && (
            <FootCount>
              <b>{impTotalRows}</b> {impTotalRows === 1 ? "row" : "rows"} ready
              to import
            </FootCount>
          )}
          <Btn onClick={onClose}>Cancel</Btn>
          {tab === "import" && impPhase === "idle" && (
            <Btn
              $primary
              disabled={!impFile && !impFiles.length}
              onClick={() => (impFiles.length ? importPdfBatch() : uploadImportFile(impFile))}
              title={impFile || impFiles.length ? "" : "Choose a file first"}
            >
              <FiUpload size={14} />{" "}
              {impFiles.length
                ? `Import ${impFiles.length} PDF${impFiles.length === 1 ? "" : "s"}`
                : impFile && !impIsPdf
                  ? "Next: map columns"
                  : "Import"}
            </Btn>
          )}
          {tab === "import" && impPhase === "mapping" && (
            <Btn
              $primary
              onClick={confirmImportMapping}
              disabled={!mappingReady || impSubmitting}
            >
              <FiPlus size={14} />{" "}
              {impSubmitting ? "Starting…" : "Start import"}
            </Btn>
          )}
          {tab === "scout" && scoutPhase === "idle" && (
            <Btn
              $primary
              onClick={trackCompany}
              data-tour="jt-submit"
              disabled={
                !scoutCompany.trim() ||
                !anySource ||
                (scoutCareers && !scoutUrl.trim()) ||
                !candsReady
              }
            >
              <Spark size={14} /> Track {scoutCompany.trim() || "company"}
            </Btn>
          )}
          {tab === "industry" && indPhase === "firms" && (
            <Btn $primary onClick={requestSaveIndustry} disabled={indFirms.length === 0}>
              <FiCheck size={14} />{" "}
              {indAdded > 0
                ? `Add ${indAdded} ${indAdded === 1 ? "firm" : "firms"} · ${indCost.toLocaleString()} credits`
                : indRemoved > 0
                  ? `Remove ${indRemoved} ${indRemoved === 1 ? "firm" : "firms"}`
                  : "Save selection"}
            </Btn>
          )}
          {tab === "industry" && indPhase === "confirm" && (
            <>
              <Btn onClick={() => setIndPhase("firms")}>Back</Btn>
              <Btn $primary onClick={saveIndustry}>
                <FiCheck size={14} />{" "}
                {indAdded > 0
                  ? `Confirm · ${indCost.toLocaleString()} credits`
                  : `Remove ${indRemoved} ${indRemoved === 1 ? "firm" : "firms"}`}
              </Btn>
            </>
          )}
        </ModalFoot>
      </Modal>
    </ModalBackdrop>,
    document.body,
  );
}


/* ─────────────────────────────────────────────────────────
   Pagination
───────────────────────────────────────────────────────── */
const Pager = styled.div`
  display: flex;
  justify-content: center;
  margin: 32px 0 8px;
  gap: 8px;
  flex-wrap: wrap;
  /* One line on phones. The buttons below shrink enough that all seven fit
     down to 320px, so First/Last stay available rather than being dropped. */
  @media (max-width: 560px) {
    flex-wrap: nowrap;
    gap: 4px;
    margin: 24px 0 8px;
  }
  @media (max-width: 390px) {
    gap: 3px;
  }
`;
// Reserves the same vertical footprint as <Pager> while the list is loading
// so content below doesn't jump when pagination appears.
const PagerSpacer = styled.div`
  margin: 32px 0 8px;
  height: 32px;
  /* Track PageBtn's height per breakpoint or the reservation stops matching. */
  @media (max-width: 560px) {
    margin: 24px 0 8px;
    height: 30px;
  }
  @media (max-width: 390px) {
    height: 28px;
  }
`;
const PageBtn = styled.button`
  height: 32px;
  min-width: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  &:hover:not(:disabled) {
    background: var(--surface-tint);
    border-color: var(--line-strong);
  }
  ${({ $active }) =>
    $active &&
    css`
      background: var(--accent-soft);
      color: var(--accent);
      border-color: color-mix(in srgb, var(--accent) 45%, transparent);
      font-weight: 600;
    `}
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* Last, so these beat the base height/padding/font-size above at equal
     specificity. flex-shrink keeps the row from compressing labels instead of
     staying on one line. */
  @media (max-width: 560px) {
    height: 30px;
    /* Global 44px touch-target floor in App.js would clamp the height. */
    min-height: 0;
    min-width: 28px;
    padding: 0 7px;
    font-size: 12px;
    flex-shrink: 0;
  }
  @media (max-width: 390px) {
    height: 28px;
    min-width: 26px;
    padding: 0 5px;
    font-size: 11px;
  }
`;

/* ─────────────────────────────────────────────────────────
   Main page
───────────────────────────────────────────────────────── */
function Jobs() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user, isAuthenticated, features: featureFlags } = useAuth();
  // File import is parked (FEATURE_JOB_IMPORT): no Add jobs button, modal opens on Find jobs.
  const jobImportOn = !!featureFlags?.jobImport;
  const scoutByIndustryOn = !!featureFlags?.scoutByIndustry;
  const [searchParams, setSearchParams] = useSearchParams();
  const pickForParam = searchParams.get("pickFor");
  const isPickerMode = pickForParam === "find-candidates";
  const [pickedJobId, setPickedJobId] = useState("");

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }, []);

  // Internal refs for fetch dedup
  const lastQueryParamsRef = useRef("");
  const lastFilterKeyRef = useRef("");
  const abortControllerRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const [urlParamsApplied, setUrlParamsApplied] = useState(0);

  // Bookmarks (from user settings)
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoaded, setBookmarksLoaded] = useState(false);
  // Each bookmark's added date, kept across saves.
  const [watchlistMeta, setWatchlistMeta] = useState({});
  const [autoWatchFilters, setAutoWatchFilters] = useState([]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setBookmarks([]);
      return;
    }
    (async () => {
      try {
        const data = await getUserSettings(user.sub);
        if (!data) return;
        const watchlistItems = data.watchlist || [];
        const jobIds = watchlistItems.map((item) =>
          typeof item.job_id === "object" && item.job_id.$oid
            ? item.job_id.$oid
            : item.job_id,
        );
        setBookmarks(jobIds);
        const meta = {};
        watchlistItems.forEach((item, i) => {
          meta[jobIds[i]] = item.added_date || null;
        });
        setWatchlistMeta(meta);
        setAutoWatchFilters(data.autoWatchFilters || []);
      } catch (err) {
        console.error("Failed to load user settings:", err);
      } finally {
        setBookmarksLoaded(true);
      }
    })();
  }, [isAuthenticated, user]);

  const saveUserSettings = useCallback(
    async (updatedBookmarks) => {
      if (!isAuthenticated || !user) return;
      try {
        const now = new Date().toISOString();
        const watchlist = updatedBookmarks.map((jobId) => ({ job_id: jobId, added_date: watchlistMeta[jobId] || now }));
        setWatchlistMeta((m) => {
          const next = { ...m };
          for (const id of updatedBookmarks) if (!next[id]) next[id] = now;
          return next;
        });
        // Update the shared cache before the POST so other pages see this immediately.
        updateCachedUserSettings(user.sub, { watchlist, autoWatchFilters });
        await fetchWithAuth(
          `/api/user-settings/${encodeURIComponent(user.sub)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ watchlist, autoWatchFilters }),
          },
        );
        invalidateUserSettingsCache(user.sub);
      } catch (err) {
        console.error("Failed to save user settings:", err);
      }
    },
    [isAuthenticated, user, autoWatchFilters, watchlistMeta],
  );

  const toggleBookmark = useCallback(
    (job) => {
      const id = jobIdOf(job);
      if (!id) return;
      const wasSaved = bookmarks.includes(id);
      const next = wasSaved
        ? bookmarks.filter((b) => b !== id)
        : [...bookmarks, id];
      setBookmarks(next);
      saveUserSettings(next);
      showToast(wasSaved ? "Removed from saved" : "Saved");
    },
    [bookmarks, saveUserSettings, showToast],
  );

  // Jobs + total
  const [jobs, setJobs] = useState([]);
  const [totalJobsCount, setTotalJobsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // URL <-> state
  const parseUrlParams = useCallback(
    (params = searchParams) => {
      const search = params.get("search") || "";
      const industry = params.get("industry") || null;
      const jobFunction = params.get("function");
      const seniority = params.get("seniority");
      const country = params.get("country");
      const source = params.get("source");
      const posted = params.get("posted");
      const page = params.get("page");
      const tab = params.get("tab") || "trackedjobs";
      const myRoles = params.get("roles") === "mine";
      const jobId = params.get("jobId") || "";
      return {
        search,
        industry: industry || null,
        jobFunction: jobFunction ? jobFunction.split("|").filter(Boolean) : [],
        seniority: seniority ? seniority.split("|").filter(Boolean) : [],
        country: country ? country.split("|").filter(Boolean) : [],
        source: source ? source.split("|").filter(Boolean) : [],
        posted: posted || null,
        page: page ? parseInt(page, 10) : 1,
        tab,
        myRoles,
        jobId,
      };
    },
    [searchParams],
  );

  const initial = parseUrlParams(searchParams);
  const [draft, setDraft] = useState({
    search: initial.search,
    industry: initial.industry,
    jobFunction: initial.jobFunction,
    seniority: initial.seniority,
    country: initial.country,
    source: initial.source,
    posted: initial.posted,
  });
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(initial.page);
  // Views: "trackedjobs" (scout + industry-pick jobs, the default), "tracked" (companies).
  // Public-corpus firms only reach the page through "Scout by industry" picks.
  const [view, setView] = useState(initial.tab === "tracked" || initial.tab === "saved" ? initial.tab : "trackedjobs");
  // My roles: the roles this user wrote in Find Candidates (clientJobs, importedBy = me). A toggle
  // beside the filters swaps the Tracked jobs list for them; `mineType` carries it to the server.
  const [myRolesOnly, setMyRolesOnly] = useState(initial.myRoles);
  const mineType = myRolesOnly ? "imported" : "tracked";
  // Every view except Tracked companies lists jobs.
  // Tracked companies lists firms and Saved jobs draws from bookmarks, so neither uses the job filters.
  const isJobList = view !== "tracked" && view !== "saved";

  // The Saved jobs tab: bookmarked postings resolved by id, archived ones included and stamped closed.
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  useEffect(() => {
    if (view !== "saved" || !bookmarksLoaded) return undefined;
    if (bookmarks.length === 0) {
      setSavedJobs([]);
      return undefined;
    }
    let cancelled = false;
    setSavedLoading(true);
    (async () => {
      try {
        const chunks = [];
        for (let i = 0; i < bookmarks.length; i += 100) chunks.push(bookmarks.slice(i, i + 100));
        const pages = await Promise.all(
          chunks.map((ids) =>
            fetchWithAuth("/api/jobs/by-ids", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ids, includeClosed: true }),
            }).then((r) => (r.ok ? r.json() : [])),
          ),
        );
        if (cancelled) return;
        const byId = new Map();
        for (const j of pages.flat()) byId.set(jobIdOf(j), j);
        // Newest bookmark first.
        setSavedJobs([...bookmarks].reverse().map((id) => byId.get(id)).filter(Boolean));
      } catch {
        if (!cancelled) setSavedJobs([]);
      } finally {
        if (!cancelled) setSavedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, bookmarks, bookmarksLoaded]);
  const [layout, setLayout] = useJobLayout();
  const [activeJobId, setActiveJobId] = useState(initial.jobId || "");
  const [activeJob, setActiveJob] = useState(() =>
    initial.jobId ? getCachedJobDetail(initial.jobId) : null,
  );
  // True while the full job detail is being fetched (shows a detail skeleton).
  const [detailLoading, setDetailLoading] = useState(
    () => !!initial.jobId && !getCachedJobDetail(initial.jobId),
  );
  const [searchInput, setSearchInput] = useState(initial.search);

  // Switch tabs. The job lists draw from different sets, so clear filters when
  // moving between them to avoid carrying over selections that don't exist there.
  function changeView(next) {
    setActiveJobId("");
    if (next === view) return;
    // A job list entered via the tab starts clean (e.g. drops a company pinned by "View jobs").
    if (next !== "tracked") {
      const empty = {
        search: "",
        industry: null,
        jobFunction: [],
        seniority: [],
        country: [],
        source: [],
        posted: null,
      };
      setDraft(empty);
      setApplied(empty);
      setSearchInput("");
      setPage(1);
      // Entering a job list (incl. from Tracked, which doesn't fetch) must always
      // pull fresh data — clear the dedup guard so the same params still refetch.
      lastQueryParamsRef.current = "";
    }
    setView(next);
  }

  const jobsPerPage = 15;

  // Filter option lists — the same three facets on every tab: industry (per
  // company) / function / seniority, plus country and company.
  const [industries, setIndustries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [seniorities, setSeniorities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);

  // Firms picked via "Scout by industry" (public-corpus firms shown in My jobs).
  // Declared here because the filter-options effect below depends on it.
  const [industrySel, setIndustrySel] = useState([]);
  const [industryLoaded, setIndustryLoaded] = useState(false);

  useEffect(() => {
    if (view === "tracked") return; // filter bar isn't shown on the Tracked tab
    // Each job tab's options come from its own set.
    const q = `?scope=mine&mineType=${mineType}`;
    fetchWithAuth(`/api/jobs/filter-options${q}`)
      .then((res) => res.json())
      .then((data) => {
        setIndustries(data.industries || []);
        setFunctions(data.functions || []);
        // Stable sort: lifts the seniority buckets into rank order.
        setSeniorities(
          [...(data.seniorities || [])].sort(
            (a, b) => seniorityRank(a) - seniorityRank(b),
          ),
        );
        setCountries(data.countries || []);
        setSources(data.sources || []);
      })
      .catch((err) => console.error("Failed to fetch filter options:", err));
  }, [view, mineType, industrySel]);

  // Tracked companies (from /api/scout/tracked)
  const [tracked, setTracked] = useState([]);
  // False until the first load settles (even a failed one), so counts show a placeholder, not 0.
  const [trackedLoaded, setTrackedLoaded] = useState(false);
  const refreshTracked = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setTracked([]);
      return;
    }
    try {
      const res = await fetchWithAuth("/api/scout/tracked");
      if (!res.ok) return;
      const data = await res.json();
      setTracked(Array.isArray(data) ? data.map(mapTrackedRow) : []);
    } catch (err) {
      console.error("Failed to load tracked companies:", err);
    } finally {
      setTrackedLoaded(true);
    }
  }, [isAuthenticated, user]);
  useEffect(() => {
    refreshTracked();
  }, [refreshTracked]);
  // Re-fetch (and keep polling) the tracked list while viewing that tab, so a
  // company flips from "Scraping…" to "Active" without a manual reload.
  useEffect(() => {
    if (view !== "tracked") return;
    refreshTracked();
    const id = setInterval(refreshTracked, 5000);
    return () => clearInterval(id);
  }, [view, refreshTracked]);

  // Count of the company's imported (file-upload) jobs — part of the My jobs badge.
  // null until the first load settles, so the tab shows a placeholder, not 0.
  const [importedCount, setImportedCount] = useState(null);
  const refreshImportedCount = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setImportedCount(null);
      return;
    }
    // Import parked: nothing can have been imported, and the server 404s the whole /jobs/import* prefix.
    if (!jobImportOn) {
      setImportedCount(0);
      return;
    }
    try {
      const res = await fetchWithAuth("/api/jobs/imported-count");
      const data = res.ok ? await res.json() : null;
      setImportedCount((prev) => (data ? Number(data.count) || 0 : prev ?? 0));
    } catch (err) {
      console.error("Failed to load imported-jobs count:", err);
      setImportedCount((prev) => prev ?? 0);
    }
  }, [isAuthenticated, user, jobImportOn]);
  useEffect(() => {
    refreshImportedCount();
  }, [refreshImportedCount]);

  const refreshIndustrySel = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setIndustrySel([]);
      return;
    }
    try {
      const res = await fetchWithAuth("/api/industries/selection");
      if (!res.ok) return;
      const data = await res.json();
      setIndustrySel(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load industry selection:", err);
    } finally {
      setIndustryLoaded(true);
    }
  }, [isAuthenticated, user]);
  useEffect(() => {
    refreshIndustrySel();
  }, [refreshIndustrySel]);

  // Add modal
  const [addOpen, setAddOpen] = useState(false);
  const [addTab, setAddTab] = useState("import");
  const [addIndustry, setAddIndustry] = useState("");
  const openAdd = (tab, industryKey = "") => {
    setAddTab(tab);
    setAddIndustry(industryKey);
    setAddOpen(true);
  };

  // File imports run as background jobs — poll quietly after the modal closes
  // and toast when the worker finishes, refreshing My jobs if it's on screen.
  const importPollTimersRef = useRef(new Set());
  useEffect(() => {
    const timers = importPollTimersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);
  const pollImportJob = useCallback(
    (jobId, attempt = 0) => {
      if (attempt > 150) return; // give up after ~10 minutes
      const t = setTimeout(async () => {
        importPollTimersRef.current.delete(t);
        try {
          const res = await fetchWithAuth(`/api/jobs/import/${jobId}/status`);
          if (res.ok) {
            const data = await res.json();
            if (data.status === "succeeded") {
              const n = data.result?.imported ?? 0;
              // Worker explains an empty import (e.g. scanned PDF) via result.message.
              showToast(
                n > 0
                  ? `Imported ${n} ${n === 1 ? "job" : "jobs"} — see Imported jobs`
                  : `Import finished — ${data.result?.message || "no jobs found in the file"}`,
              );
              refreshImportedCount();
              setMyRolesOnly(true);
              setView("trackedjobs");
              setActiveJobId("");
              setActiveJob(null);
              lastQueryParamsRef.current = "";
              setUrlParamsApplied((v) => v + 1);
              return;
            }
            if (data.status === "failed") {
              showToast(data.error || "Import failed — please try again");
              return;
            }
          }
        } catch (_) {
          /* transient poll error — keep trying */
        }
        pollImportJob(jobId, attempt + 1);
      }, 4000);
      importPollTimersRef.current.add(t);
    },
    [showToast, refreshImportedCount],
  );
  const handleImportQueued = useCallback(
    (jobId, fileName) => {
      showToast(`Importing ${fileName} in the background…`);
      pollImportJob(jobId);
    },
    [showToast, pollImportJob],
  );

  // A PDF batch is watched by the modal itself; this runs once every file is settled.
  const handleImportFinished = useCallback(
    ({ files, imported, failed, unfinished, failedNames = [] }) => {
      const parts = [`Imported ${imported} ${imported === 1 ? "job" : "jobs"} from ${files} PDF${files === 1 ? "" : "s"}`];
      if (failed) {
        const named = failedNames.slice(0, 3).join(", ");
        parts.push(`${failed} failed${named ? `: ${named}${failedNames.length > 3 ? "…" : ""}` : ""}`);
      }
      if (unfinished) parts.push(`${unfinished} still running in the background`);
      showToast(parts.join(" — "));
      refreshImportedCount();
      setMyRolesOnly(true);
      setView("trackedjobs");
      setActiveJobId("");
      setActiveJob(null);
      lastQueryParamsRef.current = "";
      setUrlParamsApplied((v) => v + 1);
    },
    [showToast, refreshImportedCount],
  );

  // Tracked actions
  const removeTrack = useCallback(
    async (id) => {
      // Industry-pick row: PUT the selection back without this firm.
      const pick = /^industry:([^:]+):(.+)$/.exec(String(id));
      if (pick) {
        const [, key, name] = pick;
        const sources = (industrySel.find((ind) => ind.key === key)?.sources || [])
          .map((f) => f.name)
          .filter((n) => n !== name);
        setIndustrySel((list) =>
          list.map((ind) =>
            ind.key === key ? { ...ind, sources: ind.sources.filter((f) => f.name !== name) } : ind,
          ),
        ); // optimistic
        try {
          await fetchWithAuth(`/api/industries/${encodeURIComponent(key)}/selection`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sources }),
          });
        } catch (err) {
          console.error("Failed to untrack industry firm:", err);
        }
        refreshIndustrySel();
        showToast("Stopped tracking");
        return;
      }
      setTracked((list) => list.filter((c) => c.id !== id)); // optimistic
      try {
        await fetchWithAuth(`/api/scout/tracked/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
      } catch (err) {
        console.error("Failed to untrack:", err);
      }
      showToast("Stopped tracking");
    },
    [showToast, industrySel, refreshIndustrySel],
  );
  // Turn a job-board pull on/off for me on a tracked row, then re-read the list.
  const togglePlatform = useCallback(
    async (id, platform, enabled) => {
      const label = PLATFORMS[platform]?.label || platform;
      try {
        const res = await fetchWithAuth(
          `/api/scout/tracked/${encodeURIComponent(id)}/platforms`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ platform, enabled }),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || `Couldn't update ${label}.`);
        showToast(enabled ? `Pulling from ${label}` : `Stopped pulling from ${label}`);
      } catch (err) {
        console.error("Failed to toggle platform:", err);
        showToast(err.message || `Couldn't update ${label}.`);
      }
      refreshTracked();
    },
    [showToast, refreshTracked],
  );
  // Called after the Scout modal tracks a company → refresh the real list.
  const startTracking = useCallback(
    (name, data) => {
      setView("tracked");
      setActiveJobId("");
      setActiveJob(null);
      if (data?.alreadyTracked) {
        showToast(`You're already tracking ${name} — it's in your list below`);
      } else if (data?.scraping) {
        showToast(`Now tracking ${name} — scraping in the background`);
      } else {
        showToast(`Now tracking ${name}`);
      }
      refreshTracked();
    },
    [showToast, refreshTracked],
  );
  const viewCompanyJobs = useCallback(
    (name) => {
      // Scout jobs live in Tracked jobs — open it filtered to just this
      // company (clean slate; no filters carried over from other tabs).
      setView("trackedjobs");
      setActiveJobId("");
      setActiveJob(null);
      const only = {
        search: "",
        industry: null,
        jobFunction: [],
        seniority: [],
        country: [],
        source: [name],
        posted: null,
      };
      setDraft(only);
      setApplied(only);
      setSearchInput("");
      setPage(1);
      lastQueryParamsRef.current = "";
      window.scrollTo({ top: 0 });
    },
    [],
  );

  // Build query string from applied filters
  const buildQueryParams = useCallback(
    (pageNum = page) => {
      const params = new URLSearchParams();
      const a = applied;
      params.append("limit", jobsPerPage.toString());
      params.append("skip", ((pageNum - 1) * jobsPerPage).toString());
      params.append("includeTotal", "true");
      params.append("searchTitleOnly", "true");
      params.append("scope", "mine");
      params.append("mineType", mineType);
      if (a.search) params.append("search", a.search);
      if (a.industry) params.append("industry", a.industry);
      if (a.jobFunction.length > 0)
        params.append("function", a.jobFunction.join("|"));
      if (a.seniority.length > 0)
        params.append("seniority", a.seniority.join("|"));
      if (a.country.length > 0) params.append("country", a.country.join("|"));
      if (a.source.length > 0) params.append("source", a.source.join("|"));
      if (a.posted) params.append("posted", a.posted);
      return params.toString();
    },
    [applied, user?.sub, page, view, mineType],
  );

  const buildFilterKey = useCallback(
    () =>
      JSON.stringify({
        ...applied,
        userId: user?.sub,
        view,
        mineType,
      }),
    [applied, user?.sub, view, mineType],
  );

  // Apply filters (commits to URL + fetch)
  const commitFilters = useCallback(
    (patch) => {
      const next = { ...draft, ...patch };
      setDraft(next);
      setApplied(next);
      setPage(1);
      lastQueryParamsRef.current = "";
    },
    [draft],
  );

  const setSearch = useCallback(
    (q) => {
      setSearchInput(q);
    },
    [],
  );
  const submitSearch = useCallback(() => {
    const next = { ...draft, search: searchInput };
    setDraft(next);
    setApplied(next);
    setPage(1);
    lastQueryParamsRef.current = "";
  }, [draft, searchInput]);

  // Fetch jobs effect. A layout effect, so a tab/page/filter change swaps to the skeleton (or the
  // cached list) before the browser paints — the previous list or an empty state never flashes.
  useLayoutEffect(() => {
    // Tracked companies lists firms and Saved jobs resolves bookmarks itself — neither calls /api/jobs.
    if (view === "tracked" || view === "saved") return;

    const currentFilterKey = buildFilterKey();
    const filtersChanged = currentFilterKey !== lastFilterKeyRef.current;

    if (filtersChanged && !isInitialMountRef.current) {
      if (page === 1) {
        lastFilterKeyRef.current = currentFilterKey;
        lastQueryParamsRef.current = "";
      } else {
        setPage(1);
        return;
      }
    }

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }

    const queryParams = buildQueryParams(page);
    if (queryParams === lastQueryParamsRef.current) {
      if (!filtersChanged) lastFilterKeyRef.current = currentFilterKey;
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    lastQueryParamsRef.current = queryParams;
    lastFilterKeyRef.current = currentFilterKey;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const cachedList = getCachedJobsList(queryParams);
    // A cached list paints straight away; the fetch below refreshes it in place.
    setLoading(!cachedList);
    if (cachedList) {
      setJobs(cachedList.jobs || []);
      setTotalJobsCount(cachedList.total || 0);
    }

    fetchWithAuth(`/api/jobs?${queryParams}`, {
      signal: abortController.signal,
    })
      .then((res) => {
        if (abortController.signal.aborted) return;
        return res.json();
      })
      .then((data) => {
        if (!data || abortController.signal.aborted) return;
        const jobsArray = data.jobs || [];
        const total = data.total || 0;
        primeJobsListCache(queryParams, { jobs: jobsArray, total });
        setJobs(jobsArray);
        setTotalJobsCount(total);
        setLoading(false);
        abortControllerRef.current = null;
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setLoading(false);
        abortControllerRef.current = null;
      });
  }, [buildQueryParams, buildFilterKey, page, urlParamsApplied, view]);

  // URL <-> applied filters sync (one-way: applied -> URL)
  useEffect(() => {
    const params = new URLSearchParams();
    const a = applied;
    if (a.search) params.set("search", a.search);
    if (a.industry) params.set("industry", a.industry);
    if (a.jobFunction.length > 0) params.set("function", a.jobFunction.join("|"));
    if (a.seniority.length > 0) params.set("seniority", a.seniority.join("|"));
    if (a.country.length > 0) params.set("country", a.country.join("|"));
    if (a.source.length > 0) params.set("source", a.source.join("|"));
    if (a.posted) params.set("posted", a.posted);
    if (isPickerMode) params.set("pickFor", "find-candidates");
    if (view !== "trackedjobs") params.set("tab", view);
    if (myRolesOnly && view === "trackedjobs") params.set("roles", "mine");
    if (page > 1) params.set("page", page.toString());
    if (activeJobId) params.set("jobId", activeJobId);
    const currentParams = searchParams.toString();
    const newParams = params.toString();
    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
    }
  }, [
    applied,
    user?.sub,
    page,
    searchParams,
    setSearchParams,
    isPickerMode,
    view,
    myRolesOnly,
    activeJobId,
  ]);

  // Reset filters
  const resetFilters = useCallback(() => {
    const cleared = {
      search: "",
      industry: null,
      jobFunction: [],
      seniority: [],
      country: [],
      source: [],
      posted: null,
    };
    setDraft(cleared);
    setApplied(cleared);
    setSearchInput("");
    setPage(1);
    lastQueryParamsRef.current = "";
  }, []);

  // Load detail job when activeJobId is set
  useEffect(() => {
    if (!activeJobId) {
      setActiveJob(null);
      setDetailLoading(false);
      return;
    }
    const cached = getCachedJobDetail(activeJobId);
    if (cached) {
      setActiveJob(cached);
      setDetailLoading(false);
    } else {
      setActiveJob(null);
      setDetailLoading(true);
    }
    let cancelled = false;
    getJobDetail(activeJobId, { forceRefresh: !cached })
      .then((data) => {
        if (cancelled) return;
        setActiveJob(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeJobId]);

  // When opening a job, scroll to top
  // Stash the grid scroll position so we can restore it when the user
  // backs out of a detail view. Cleared on filter/page changes.
  const gridScrollRef = useRef(0);
  const restoreScrollRef = useRef(null);
  const openJobDetail = useCallback((job) => {
    const id = jobIdOf(job);
    if (!id) return;
    gridScrollRef.current = window.scrollY || 0;
    primeJobDetailCache(job);
    setActiveJob(job);
    setActiveJobId(id);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const openJob = useCallback(
    (job) => {
      // Picker mode: card click selects; "View details" still opens the detail.
      if (isPickerMode) {
        setPickedJobId(jobIdOf(job));
        return;
      }
      openJobDetail(job);
    },
    [isPickerMode, openJobDetail],
  );
  const closeJob = useCallback(() => {
    // If the user landed here from another page (e.g. clicked a row in
    // ResearchPage), pop back to wherever they came from instead of just
    // closing the inline detail view.
    if (routerLocation.state?.fromExternal) {
      navigate(-1);
      return;
    }
    restoreScrollRef.current = gridScrollRef.current || 0;
    setActiveJob(null);
    setActiveJobId("");
  }, [navigate, routerLocation.state]);

  // Restore grid scroll once the grid is back on screen.
  useEffect(() => {
    if (activeJob || activeJobId) return;
    if (restoreScrollRef.current == null) return;
    const y = restoreScrollRef.current;
    restoreScrollRef.current = null;
    // Wait one frame so the grid has laid out before scrolling.
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: "auto" });
    });
  }, [activeJob, activeJobId]);

  // Pagination: snap to top instantly whenever `page` changes
  // (but not on the very first mount / URL restore).
  const prevPageRef = useRef(page);
  useEffect(() => {
    if (prevPageRef.current !== page) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    prevPageRef.current = page;
  }, [page]);

  // Header counters: new jobs today / this week across the user's own jobs (tracked,
  // industry picks, imported). `jobs` only holds the current page, so we ask the server
  // for totals; the public corpus is never counted here.
  const [postedCounts, setPostedCounts] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    const countPosted = (days) =>
      fetchWithAuth(`/api/jobs?scope=mine&posted=${days}&limit=1&includeTotal=true`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => (data && typeof data.total === "number" ? data.total : 0))
        .catch(() => 0);
    let alive = true;
    Promise.all([countPosted(0), countPosted(7)]).then(([today, week]) => {
      if (alive) setPostedCounts({ today, week });
    });
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);
  const newToday = postedCounts?.today || 0;
  const newThisWeek = postedCounts?.week || 0;
  // "My jobs" tab count = scout jobs across the user's tracked companies plus
  // the company's file-imported jobs. Only count companies that have finished
  // their first scrape — a company still syncing shouldn't inflate the badge.
  const industryJobsCount = useMemo(
    () => industrySel.reduce((s, ind) => s + (Number(ind.jobCount) || 0), 0),
    [industrySel],
  );
  const trackedJobsCount = useMemo(
    () =>
      tracked.reduce(
        (s, c) => (c.status === "syncing" ? s : s + (Number(c.totalJobs) || 0)),
        industryJobsCount,
      ),
    [tracked, industryJobsCount],
  );

  // After the industry picker saves: land on Tracked jobs with a fresh fetch.
  const handleIndustrySaved = useCallback(
    (data) => {
      const n = Number(data?.selected) || 0;
      showToast(
        n > 0
          ? `${n} ${data?.label || ""} ${n === 1 ? "firm" : "firms"} added — their roles are in Tracked jobs`
          : `Cleared your ${data?.label || "industry"} firms`,
      );
      refreshIndustrySel();
      setView("trackedjobs");
      setActiveJobId("");
      setActiveJob(null);
      lastQueryParamsRef.current = "";
      setUrlParamsApplied((v) => v + 1);
    },
    [showToast, refreshIndustrySel],
  );

  // Source candidates from job detail
  const sourceFromJob = useCallback(
    (job) => {
      const id = jobIdOf(job);
      const countryValue = Array.isArray(job.country)
        ? job.country[0]
        : job.country;
      navigate("/find-candidates", {
        state: {
          selectedJob: {
            ...job,
            id,
            title: job.title || job.job_title || "",
            description: job.description || "",
            country: countryValue || "",
          },
        },
      });
    },
    [navigate],
  );

  // Total pages
  const totalPages =
    totalJobsCount > 0 ? Math.ceil(totalJobsCount / jobsPerPage) : 1;

  // Picker select handler
  const confirmPick = useCallback(() => {
    const selected = jobs.find((j) => String(jobIdOf(j)) === String(pickedJobId));
    if (!selected) return;
    sourceFromJob(selected);
  }, [jobs, pickedJobId, sourceFromJob]);

  // ── Render ──
  const hasFilters =
    !!applied.search ||
    !!applied.industry ||
    applied.jobFunction.length > 0 ||
    applied.seniority.length > 0 ||
    applied.country.length > 0 ||
    applied.source.length > 0 ||
    !!applied.posted;

  const visibleJobs = jobs;
  // The list total is unknown on Tracked companies until a job tab has loaded — leave it out there.
  const showPositions = isJobList || !loading;

  return (
    <Shell>
      <Top $crumbs={!!activeJob}>
        <TopRow>
          <TopLead>
            {/* Only inside a job: the page title already names the list. */}
            {activeJob && (
              <Crumbs>
                <Crumb onClick={closeJob}>Find jobs</Crumb>
                <CrumbSep>/</CrumbSep>
                <Crumb $current>
                  {activeJob.title || activeJob.job_title || "Job"}
                </Crumb>
              </Crumbs>
            )}
            <PageTitle>
              Find jobs
              <TitleMeta>
                {showPositions && (
                  <>
                    {loading ? <CountSkeleton width={26} /> : <b>{totalJobsCount.toLocaleString()}</b>}{" "}
                    positions
                  </>
                )}
                {!activeJob && newToday > 0 && (
                  <>
                    {showPositions && " · "}
                    <span className="accent">
                      <b>+{newToday}</b>
                    </span>{" "}
                    today
                  </>
                )}
                {!activeJob && (
                  <>
                    {(showPositions || newToday > 0) && " · "}
                    {postedCounts ? <b>{newThisWeek}</b> : <CountSkeleton />} this week ·{" "}
                    {bookmarksLoaded ? <b>{bookmarks.length}</b> : <CountSkeleton />} saved
                  </>
                )}
              </TitleMeta>
            </PageTitle>
          </TopLead>
          {!activeJob && (
            <TopActions data-tour="jobs-track">
              {/* With file import parked, "Add jobs" means tracking a company. */}
              <Btn $primary onClick={() => openAdd(jobImportOn ? "import" : "scout")}>
                <FiPlus size={14} /> Add jobs
              </Btn>
            </TopActions>
          )}
        </TopRow>

        {!activeJob && (
          <Tabs>
            <Tab $active={view === "trackedjobs"} onClick={() => changeView("trackedjobs")} data-tour="jobs-tab-trackedjobs">
              <Spark size={12} /> Tracked jobs{" "}
              <TabCount>
                {trackedLoaded && industryLoaded ? trackedJobsCount.toLocaleString() : <CountSkeleton />}
              </TabCount>
            </Tab>
            <Tab $active={view === "saved"} onClick={() => changeView("saved")} data-tour="jobs-tab-saved">
              <FiBookmark size={12} /> Saved jobs{" "}
              <TabCount>{bookmarksLoaded ? bookmarks.length : <CountSkeleton />}</TabCount>
            </Tab>
            <Tab
              $active={view === "tracked"}
              onClick={() => changeView("tracked")}
            >
              <FiRefreshCw size={12} /> Tracked companies{" "}
              <TabCount>
                {trackedLoaded ? new Set(tracked.map((t) => t.name)).size : <CountSkeleton />}
              </TabCount>
            </Tab>
          </Tabs>
        )}

        {!activeJob && isJobList && (
          <SearchWrap>
            <Search>
              <FiSearch className="lead" size={14} />
              <input
                value={searchInput}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitSearch()}
                placeholder="Search by title…"
                aria-label="Search jobs"
              />
            </Search>
            <FilterRow>
              <FiltersPopover
                activeCount={
                  (draft.industry ? 1 : 0) +
                  (draft.jobFunction?.length ? 1 : 0) +
                  (draft.seniority?.length ? 1 : 0) +
                  (draft.country?.length ? 1 : 0) +
                  (draft.source?.length ? 1 : 0) +
                  (draft.posted ? 1 : 0)
                }
              >
                <FilterField>
                  <PanelFieldLabel>Industry</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Industry"
                    values={draft.industry ? [draft.industry] : []}
                    options={industries}
                    single
                    onChange={(arr) => commitFilters({ industry: arr[0] || null })}
                  />
                </FilterField>
                <FilterField>
                  <PanelFieldLabel>Function</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Function"
                    values={draft.jobFunction}
                    options={functions}
                    onChange={(arr) => commitFilters({ jobFunction: arr })}
                  />
                </FilterField>
                <FilterField>
                  <PanelFieldLabel>Seniority</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Seniority"
                    values={draft.seniority}
                    options={seniorities}
                    onChange={(arr) => commitFilters({ seniority: arr })}
                  />
                </FilterField>
                <FilterField>
                  <PanelFieldLabel>Country</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Country"
                    values={draft.country}
                    options={countries}
                    onChange={(arr) => commitFilters({ country: arr })}
                  />
                </FilterField>
                <FilterField>
                  <PanelFieldLabel>Company</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Company"
                    values={draft.source}
                    options={sources}
                    onChange={(arr) => commitFilters({ source: arr })}
                  />
                </FilterField>
                <FilterField>
                  <PanelFieldLabel>Posted</PanelFieldLabel>
                  <FilterDropdown
                    block
                    label="Posted"
                    values={
                      draft.posted
                        ? [
                            POSTED_OPTIONS.find((o) => o.value === draft.posted)
                              ?.label || draft.posted,
                          ]
                        : []
                    }
                    options={POSTED_OPTIONS.map((o) => o.label)}
                    single
                    onChange={(arr) => {
                      const lbl = arr[0];
                      const opt = POSTED_OPTIONS.find((o) => o.label === lbl);
                      commitFilters({ posted: opt ? opt.value : null });
                    }}
                  />
                </FilterField>
                {hasFilters && (
                  <ClearLink onClick={resetFilters}>
                    <FiX size={12} /> Clear all filters
                  </ClearLink>
                )}
              </FiltersPopover>
              {view === "trackedjobs" && (
                <Chip
                  $active={myRolesOnly}
                  onClick={() => {
                    setMyRolesOnly((v) => !v);
                    setPage(1);
                    lastQueryParamsRef.current = "";
                  }}
                  title="Roles you wrote in Find Candidates"
                  aria-pressed={myRolesOnly}
                >
                  <FiFileText size={12} /> My roles
                  {importedCount != null && importedCount > 0 && <TabCount>{importedCount.toLocaleString()}</TabCount>}
                </Chip>
              )}
            </FilterRow>
          </SearchWrap>
        )}
      </Top>

      {/* Every job grid is pickable — "tracked" shows companies, not jobs, so it's excluded. */}
      {isPickerMode && !activeJob && isJobList && (
        <PickerBar>
          <div style={{ fontWeight: 600 }}>
            Pick one job for candidate search{pickedJobId ? " — selected" : ""}.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn
              onClick={() =>
                navigate("/find-candidates", {
                  state: { resumeSource: true },
                })
              }
            >
              ← Back to search
            </Btn>
            <Btn $primary disabled={!pickedJobId} onClick={confirmPick}>
              <FiCheck size={14} /> Select & continue
            </Btn>
          </div>
        </PickerBar>
      )}

      <Canvas>
        {activeJobId && (detailLoading || !activeJob) ? (
          <JobDetailSkeleton />
        ) : activeJob ? (
          <FadeIn>
            <JobDetailView
              job={activeJob}
              saved={bookmarks.includes(jobIdOf(activeJob))}
              onBack={closeJob}
              onToggleSave={toggleBookmark}
              onSourceCandidates={() => sourceFromJob(activeJob)}
              backLabel={routerLocation.state?.fromLabel || "jobs"}
            />
          </FadeIn>
        ) : view === "saved" ? (
          !bookmarksLoaded || savedLoading ? (
            <SavedJobsSkeleton />
          ) : (
            <FadeIn data-tour="saved-list">
              <SavedJobsView
                jobs={savedJobs}
                onUnsave={toggleBookmark}
                onOpen={openJob}
              />
            </FadeIn>
          )
        ) : view === "tracked" ? (
          <TrackedCompaniesView
            list={tracked}
            industries={industrySel}
            loading={!trackedLoaded || !industryLoaded}
            onRemove={removeTrack}
            onAddCompany={() => openAdd("scout")}
            onViewJobs={viewCompanyJobs}
            onTogglePlatform={togglePlatform}
          />
        ) : (
          <GridWrap>
            <CountRow>
              <span>
                {(() => {
                  if (loading) {
                    // Placeholder for the count text so the row keeps its height.
                    return <Skeleton width={180} height={13} radius="6px" />;
                  }
                  if (totalJobsCount === 0) {
                    // Both job tabs show their own empty state below — no count text here.
                    return null;
                  }
                  const start = (page - 1) * jobsPerPage + 1;
                  const end = Math.min(
                    page * jobsPerPage,
                    totalJobsCount,
                  );
                  return (
                    <>
                      Showing <b>{start.toLocaleString()}–
                      {end.toLocaleString()}</b> of{" "}
                      <b>{totalJobsCount.toLocaleString()}</b> jobs
                    </>
                  );
                })()}
              </span>
              {/* Layout toggle sits with the count; hidden when there's
                  nothing to lay out (an empty state renders below instead). */}
              {(loading || visibleJobs.length > 0) && (
                <>
                  <span style={{ flex: 1 }} />
                  <JobLayoutToggle value={layout} onChange={setLayout} />
                </>
              )}
            </CountRow>
            {loading ? (
              <>
                {/* Skeletons fill the same space as real cards/rows; the pager
                    spacer reserves pagination height so nothing jumps. */}
                {layout === "rows" ? (
                  <JobListSkeleton />
                ) : (
                  <JobGridSkeleton />
                )}
                <PagerSpacer />
              </>
            ) : visibleJobs.length === 0 ? (
              view === "trackedjobs" && myRolesOnly && !hasFilters ? (
                <Empty>
                  <div>
                    <div className="art">
                      <FiFileText size={42} strokeWidth={1.4} />
                    </div>
                    <h3>No roles of your own yet</h3>
                    <p>
                      Start a candidate search from a job description or a description of the
                      person, and the role lands here.
                    </p>
                    {jobImportOn && (
                      <Btn $primary onClick={() => openAdd("import")}>
                        <FiUpload size={14} /> Import jobs
                      </Btn>
                    )}
                  </div>
                </Empty>
              ) : view === "trackedjobs" && !hasFilters ? (
                <Empty>
                  <div>
                    <div className="art">
                      <FiBriefcase size={42} strokeWidth={1.4} />
                    </div>
                    <h3>No jobs yet</h3>
                    <p>
                      Find jobs from a company's careers URL and their roles
                      will appear here.
                    </p>
                    <Btn $primary onClick={() => openAdd("scout")}>
                      <Spark size={14} /> Find jobs
                    </Btn>
                  </div>
                </Empty>
              ) : (
                <Empty>
                  <div>
                    <div className="art">
                      <FiSearch size={42} strokeWidth={1.4} />
                    </div>
                    <h3>No jobs match those filters</h3>
                    <p>Try clearing filters or broadening your search.</p>
                    <Btn onClick={resetFilters}>Clear filters</Btn>
                  </div>
                </Empty>
              )
            ) : (
              <FadeIn>
                {(() => {
                  const List = layout === "rows" ? JobRows : Grid;
                  const Item = layout === "rows" ? JobRowCard : JobGridCard;
                  return (
                    <List>
                      {visibleJobs.map((j) => {
                        const id = jobIdOf(j);
                        const isSaved = bookmarks.includes(id);
                        const isPicked =
                          isPickerMode && String(pickedJobId) === String(id);
                        return (
                          <Item
                            key={id}
                            job={j}
                            saved={isSaved}
                            picked={isPicked}
                            pickerMode={isPickerMode}
                            onOpen={openJob}
                            onViewDetails={openJobDetail}
                            onToggleSave={toggleBookmark}
                          />
                        );
                      })}
                    </List>
                  );
                })()}
                {totalPages > 1 && (
                  <Pager>
                    <PageBtn
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                    >
                      First
                    </PageBtn>
                    <PageBtn
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Prev
                    </PageBtn>
                    {(() => {
                      let start = Math.max(
                        1,
                        Math.min(page - 1, totalPages - 2),
                      );
                      let end = Math.min(totalPages, start + 2);
                      if (end - start < 2) start = Math.max(1, end - 2);
                      return Array.from(
                        { length: Math.min(3, totalPages) },
                        (_, i) => {
                          const n = start + i;
                          return (
                            <PageBtn
                              key={n}
                              onClick={() => setPage(n)}
                              $active={page === n}
                            >
                              {n}
                            </PageBtn>
                          );
                        },
                      );
                    })()}
                    <PageBtn
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page === totalPages}
                    >
                      Next
                    </PageBtn>
                    <PageBtn
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                    >
                      Last
                    </PageBtn>
                  </Pager>
                )}
              </FadeIn>
            )}
          </GridWrap>
        )}
      </Canvas>

      {/* Toast */}
      {toast && (
        <Toast>
          <FiCheck size={14} strokeWidth={3} style={{ color: "var(--success)" }} />
          {toast}
        </Toast>
      )}

      {/* Add Jobs modal */}
      <AddJobsModal
        open={addOpen}
        importEnabled={jobImportOn}
        industryEnabled={scoutByIndustryOn}
        initialTab={!jobImportOn && addTab === "import" ? "scout" : addTab}
        initialIndustry={addIndustry}
        onClose={() => setAddOpen(false)}
        onQueued={handleImportQueued}
        onImportFinished={handleImportFinished}
        onTrack={startTracking}
        onIndustrySaved={handleIndustrySaved}
      />

    </Shell>
  );
}

export default Jobs;
